import React, { useState, useEffect, useCallback, useRef } from "react";
import SurveyWizard from "./SurveyWizard";
import InviteMatchingModal from "./InviteMatchingModal";
import { downloadReportPdf } from "./ReportPdf";

// ── Design tokens (mirrored from App.js) ──────────────────────────────────────
const C = {
  bg:"#06080F",surface:"#0e1117",card:"#141820",border:"#1e2533",
  accent:"#F59E0B",accentDim:"#F59E0B22",text:"#F1F5F9",muted:"#64748B",
  danger:"#EF4444",success:"#22C55E",info:"#3B82F6",purple:"#8B5CF6",teal:"#22D3EE",
};

const CC_STYLES = `
  * { box-sizing: border-box; }
  .cc-header        { padding: 14px 24px; display: flex; align-items: center; gap: 16px; background: ${C.surface}; border-bottom: 1px solid ${C.border}; position: sticky; top: 0; z-index: 10; }
  .cc-header-meta   { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .cc-tab-bar       { display: flex; overflow-x: auto; scrollbar-width: none; padding: 0 24px; background: ${C.surface}; border-bottom: 1px solid ${C.border}; position: sticky; top: 57px; z-index: 9; }
  .cc-tab-bar::-webkit-scrollbar { display: none; }
  .cc-body          { padding: 24px; max-width: 1200px; margin: 0 auto; }
  .cc-stats-grid    { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 20px; }
  .cc-detail-grid   { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
  .cc-table         { width: 100%; border-collapse: collapse; background: ${C.card}; }
  .cc-thead         { display: table-header-group; }
  .cc-tr            { display: table-row; }
  .cc-th, .cc-td    { display: table-cell; }
  .cc-card-label    { display: none; }
  .cc-inv-lines-grid { display: grid; grid-template-columns: 1fr 80px 100px 32px; gap: 8px; margin-bottom: 8px; align-items: end; }
  input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.8); cursor: pointer; }
  input[type="date"]::-webkit-inner-spin-button { display: none; }

  @media (max-width: 768px) {
    .cc-header        { padding: 12px 16px; gap: 10px; top: 0; }
    .cc-tab-bar       { top: 53px; }
    .cc-header-meta   { gap: 8px; }
    .cc-body          { padding: 20px 14px 14px; }
    .cc-stats-grid    { grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .cc-detail-grid   { grid-template-columns: 1fr; gap: 10px; }

    /* Table → card */
    .cc-table-wrap    { border: none !important; border-radius: 0 !important; }
    .cc-table         { display: block; }
    .cc-thead         { display: none; }
    .cc-tbody         { display: flex; flex-direction: column; gap: 10px; }
    .cc-tr            { display: flex; flex-direction: column; background: ${C.card}; border: 1px solid ${C.border}; border-radius: 10px; padding: 12px 14px; gap: 5px; cursor: pointer; }
    .cc-th, .cc-td    { display: block; padding: 0 !important; border: none !important; font-size: 13px; }
    .cc-card-label    { display: inline; font-size: 10px; font-weight: 700; color: ${C.accent}; text-transform: uppercase; letter-spacing: 0.5px; margin-right: 6px; }
    .cc-td-actions    { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }

    /* Invoice line items */
    .cc-inv-lines-grid { grid-template-columns: 1fr 60px; gap: 6px; }
    .cc-inv-line-unit  { display: none; }
  }

  @media (max-width: 480px) {
    .cc-stats-grid    { grid-template-columns: repeat(2, 1fr); }
    .cc-body          { padding: 16px 10px 10px; }
    .cc-tab-bar       { top: 49px; }
  }
`;

async function api(method, path, body) {
  const res = await fetch(`/api/admin${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  // Read as text first so non-JSON bodies (e.g. a 504/502 HTML gateway page)
  // surface as a clear message instead of "Unexpected token '<'".
  const raw = await res.text();
  let data;
  try { data = raw ? JSON.parse(raw) : {}; }
  catch {
    if (res.status === 504) throw new Error("The server took too long to respond. Please try again.");
    throw new Error(`HTTP ${res.status}: server returned an unexpected response.`);
  }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

async function apiUpload(path, formData) {
  const res = await fetch(`/api/admin${path}`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  const raw = await res.text();
  let data;
  try { data = raw ? JSON.parse(raw) : {}; }
  catch {
    if (res.status === 504) throw new Error("The server took too long to respond. Please try again.");
    throw new Error(`HTTP ${res.status}: server returned an unexpected response.`);
  }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

const fmt  = d => { if(!d) return "—"; return new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); };
const inr  = n => `₹${Number(n||0).toLocaleString("en-IN")}`;
const slug = s => (s||"").replace(/_/g," ");

function Btn({children,onClick,variant="primary",disabled,style,type="button"}) {
  const v={primary:{background:C.accent,color:"#000"},outline:{background:"transparent",color:C.accent,border:`1.5px solid ${C.accent}`},ghost:{background:"transparent",color:C.muted,border:`1.5px solid ${C.border}`},danger:{background:C.danger,color:"#fff"},success:{background:C.success,color:"#000"},sm:{background:C.accent,color:"#000",padding:"6px 16px",fontSize:13},smGhost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`,padding:"6px 14px",fontSize:13},smDanger:{background:C.danger,color:"#fff",padding:"6px 14px",fontSize:13},smSuccess:{background:C.success,color:"#000",padding:"6px 14px",fontSize:13},smInfo:{background:C.info,color:"#fff",padding:"6px 14px",fontSize:13}};
  return <button type={type} disabled={disabled} onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:6,fontSize:13,fontWeight:600,cursor:disabled?"not-allowed":"pointer",border:"none",fontFamily:"inherit",opacity:disabled?0.5:1,...(v[variant]||v.primary),...style}}>{children}</button>;
}

function Badge({label,color="grey"}) {
  const m={green:{bg:"#22C55E22",text:"#22C55E"},red:{bg:"#EF444422",text:"#EF4444"},yellow:{bg:"#F59E0B22",text:"#F59E0B"},blue:{bg:"#3B82F622",text:"#3B82F6"},purple:{bg:"#8B5CF622",text:"#8B5CF6"},grey:{bg:"#64748B22",text:"#64748B"}};
  const{bg,text}=m[color]||m.grey;
  return <span style={{background:bg,color:text,padding:"2px 8px",borderRadius:99,fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{label}</span>;
}

function Spinner() { return <div style={{display:"flex",justifyContent:"center",padding:32}}><div style={{width:24,height:24,borderRadius:"50%",border:`3px solid ${C.border}`,borderTopColor:C.accent,animation:"spin 0.7s linear infinite"}}/><style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style></div>; }
function EmptyState({message}) { return <div style={{padding:"32px 0",textAlign:"center",color:C.muted,fontSize:13}}>{message}</div>; }
function Th({children,style}) { return <th style={{padding:"9px 12px",textAlign:"left",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.8,borderBottom:`1px solid ${C.border}`,background:C.surface,...style}}>{children}</th>; }
function Td({children,style,label,className}) { return <td className={`cc-td${className?" "+className:""}`} style={{padding:"9px 12px",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}`,verticalAlign:"middle",...style}}>{label&&<span className="cc-card-label">{label}</span>}{children}</td>; }
function TableWrap({children}) { return <div className="cc-table-wrap" style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}><table className="cc-table" style={{width:"100%",borderCollapse:"collapse",background:C.card}}>{children}</table></div>; }

function Section({title,children}) { return <div style={{marginBottom:24}}><div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12,paddingBottom:6,borderBottom:`1px solid ${C.border}`}}>{title}</div>{children}</div>; }

function Modal({title,onClose,children,width=440}) {
  return <div style={{position:"fixed",inset:0,background:"#000b",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"0"}} onClick={onClose}><div className="rc-modal-inner" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28,width:"100%",maxWidth:width,scrollbarWidth:"none"}} onClick={e=>e.stopPropagation()}><style>{"div::-webkit-scrollbar{display:none}.rc-modal-inner{max-height:92vh;overflow-y:auto;}@media(max-width:768px){.rc-modal-inner{max-width:100%;border-radius:0;max-height:100dvh;}}"}</style><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><span style={{fontWeight:700,fontSize:15,color:C.text}}>{title}</span><button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:28,cursor:"pointer",padding:8,minWidth:52,minHeight:52,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,lineHeight:1}}>×</button></div>{children}</div></div>;
}

function Field({label,children}) {
  return <div style={{marginBottom:14}}><label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.8,marginBottom:5}}>{label}</label>{children}</div>;
}

const INP = { width:"100%",padding:"9px 11px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none" };
const SEL = { ...{ width:"100%",padding:"9px 11px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none" } };
const TA  = { ...{ width:"100%",padding:"9px 11px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",outline:"none",resize:"vertical" } };

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ customer }) {
  // Use enrichInvoice-computed fields (paidAmount/totalAmount) for consistency with InvoicesTab
  const totalInvoiced = (customer.invoices||[]).reduce((s,i)=>s+Number(i.totalAmount||i.total||0),0);
  const totalPaid     = (customer.invoices||[]).reduce((s,i)=>s+Number(i.paidAmount||0),0);
  // "Open" = any invoice not yet fully settled (draft, sent, partial, overdue)
  const openInvoices  = (customer.invoices||[]).filter(i=>i.status!=="paid"&&i.status!=="cancelled").length;
  // Count surveys from projects (what's visible in the Projects tab), not _count.surveys
  const surveysCount  = (customer.projects||[]).flatMap(p=>p.surveys||[]).length;
  const expiringSoon  = (customer.documents||[]).filter(d=>{ if(!d.expiresAt) return false; const days=(new Date(d.expiresAt)-new Date())/86400000; return days>=0&&days<=30; });

  // Build full billing address string
  const addrParts = [customer.billingAddressLine1, customer.billingAddressLine2, customer.billingCity, customer.billingState, customer.billingPincode, customer.billingCountry!=="India"?customer.billingCountry:null].filter(Boolean);

  return (
    <div>
      <div className="cc-stats-grid" style={{marginBottom:20}}>
        {[["Projects",(customer.projects||[]).length,null],["Surveys",surveysCount,null],["Total Invoiced",inr(totalInvoiced),C.accent],["Collected",inr(totalPaid),C.success],["Open Invoices",openInvoices,openInvoices>0?C.danger:C.text],["Contacts",(customer.contacts||[]).length,null]].map(([label,val,color])=>(
          <div key={label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",minWidth:0}}>
            <div style={{fontSize:10,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
            <div style={{fontSize:22,fontWeight:700,color:color||C.text}}>{val}</div>
          </div>
        ))}
      </div>

      {expiringSoon.length>0&&(
        <div style={{background:"#F59E0B11",border:`1px solid #F59E0B44`,borderRadius:8,padding:"12px 16px",marginBottom:20}}>
          <div style={{fontWeight:700,color:C.accent,marginBottom:8}}>⚠ Expiring within 30 days</div>
          {expiringSoon.map(d=><div key={d.id} style={{fontSize:13,color:C.text,marginBottom:4}}>{d.name} ({d.type.toUpperCase()}) — expires {fmt(d.expiresAt)}</div>)}
        </div>
      )}

      <div className="cc-detail-grid">
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.8}}>Customer Details</div>
          <div style={{fontSize:13,lineHeight:2,color:C.text}}>
            <div><span style={{color:C.muted}}>Company: </span>{customer.companyName}</div>
            <div><span style={{color:C.muted}}>Plan: </span><Badge label={customer.plan} color={{starter:"grey",professional:"blue",enterprise:"purple"}[customer.plan]||"grey"}/></div>
            {customer.industry&&<div><span style={{color:C.muted}}>Industry: </span>{customer.industry}</div>}
            {customer.companySize&&<div><span style={{color:C.muted}}>Size: </span>{customer.companySize}</div>}
            {customer.billingEntityName&&<div><span style={{color:C.muted}}>Billing Entity: </span>{customer.billingEntityName}</div>}
            {addrParts.length>0&&<div><span style={{color:C.muted}}>Address: </span>{addrParts.join(", ")}</div>}
            {customer.gstin&&<div><span style={{color:C.muted}}>GSTIN: </span><span style={{fontFamily:"monospace",fontSize:12}}>{customer.gstin}</span></div>}
            {customer.pan&&<div><span style={{color:C.muted}}>PAN: </span><span style={{fontFamily:"monospace",fontSize:12}}>{customer.pan}</span></div>}
            {customer.website&&<div><span style={{color:C.muted}}>Website: </span><a href={customer.website} target="_blank" rel="noreferrer" style={{color:C.accent}}>{customer.website}</a></div>}
            <div><span style={{color:C.muted}}>Customer since: </span>{fmt(customer.createdAt)}</div>
          </div>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:0.8}}>Primary Contact</div>
          {(()=>{const p=(customer.contacts||[]).find(c=>c.isPrimary)||(customer.contacts||[])[0]; if(!p) return <div style={{color:C.muted,fontSize:13}}>No contacts added yet.</div>; return <div style={{fontSize:13,lineHeight:2,color:C.text}}><div style={{fontWeight:600}}>{p.firstName} {p.lastName}</div>{p.role&&<div style={{color:C.muted}}>{p.role}</div>}{p.email&&<div>{p.email}</div>}{p.phone&&<div>{p.phone}</div>}</div>; })()}
        </div>
      </div>

      {customer.notes&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginTop:16}}><div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:0.8}}>Notes</div><div style={{fontSize:13,color:C.text,whiteSpace:"pre-wrap",lineHeight:1.6}}>{customer.notes}</div></div>}
    </div>
  );
}

// ── Contacts Tab ──────────────────────────────────────────────────────────────
function ContactsTab({ customer, onRefresh }) {
  const [modal, setModal]   = useState(null); // null | "add" | contact object
  const [form,  setForm]    = useState({});
  const [saving, setSaving] = useState(false);
  const [msg,   setMsg]     = useState("");
  const [emailModal, setEmailModal] = useState(null);
  const [emailForm, setEmailForm]   = useState({ type:"contract_expiry", adminNote:"", docName:"", expiresAt:"", projectName:"", invoiceNum:"", amount:"", dueAt:"" });
  const [sending, setSending] = useState(false);

  function openAdd()   { setForm({ isPrimary: false }); setModal("add"); }
  function openEdit(c) { setForm({ ...c }); setModal(c); }
  function closeModal(){ setModal(null); setForm({}); setMsg(""); }

  async function save() {
    setSaving(true); setMsg("");
    try {
      const isNew = modal === "add";
      if (isNew) await api("POST", `/customers/${customer.id}/contacts`, form);
      else        await api("PATCH",`/customers/${customer.id}/contacts/${modal.id}`, form);
      onRefresh(); closeModal();
    } catch(e){ setMsg(e.message); } finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this contact?")) return;
    try { await api("DELETE",`/customers/${customer.id}/contacts/${id}`); onRefresh(); }
    catch(e){ alert(e.message); }
  }

  async function sendEmail() {
    setSending(true);
    try {
      const payload = emailModal.type==="contract_expiry" ? { docName: emailForm.docName, expiresAt: emailForm.expiresAt, adminNote: emailForm.adminNote } :
                      emailModal.type==="sow_followup"    ? { projectName: emailForm.projectName, adminNote: emailForm.adminNote } :
                      { invoiceNum: emailForm.invoiceNum, amount: emailForm.amount, dueAt: emailForm.dueAt, adminNote: emailForm.adminNote };
      await api("POST",`/customers/${customer.id}/email`,{ type: emailModal.type, contactId: emailModal.contact.id, payload });
      setEmailModal(null); setMsg("Email sent!");  setTimeout(()=>setMsg(""),3000);
    } catch(e){ setMsg(e.message); } finally { setSending(false); }
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
        {msg&&<span style={{color:C.success,fontSize:13}}>{msg}</span>}
        <Btn variant="sm" onClick={openAdd} style={{marginLeft:"auto"}}>+ Add Contact</Btn>
      </div>
      {(customer.contacts||[]).length===0?<EmptyState message="No contacts yet"/>:(
        <div className="cc-table-wrap" style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}>
          <table className="cc-table"><thead className="cc-thead"><tr className="cc-tr"><Th>Name</Th><Th>Role</Th><Th>Email</Th><Th>Phone</Th><Th>Primary</Th><Th>Actions</Th></tr></thead>
          <tbody className="cc-tbody">
            {(customer.contacts||[]).map(c=>(
              <tr key={c.id} className="cc-tr">
                <Td label="Name"><span style={{fontWeight:600}}>{c.firstName} {c.lastName}</span></Td>
                <Td label="Role" style={{color:C.muted}}>{c.role||"—"}</Td>
                <Td label="Email" style={{color:C.muted}}>{c.email||"—"}</Td>
                <Td label="Phone" style={{color:C.muted}}>{c.phone||"—"}</Td>
                <Td label="Primary">{c.isPrimary&&<Badge label="Primary" color="green"/>}</Td>
                <Td className="cc-td cc-td-actions">
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <Btn variant="smGhost" onClick={()=>openEdit(c)}>Edit</Btn>
                    {c.email&&<Btn variant="smInfo" onClick={()=>setEmailModal({contact:c,type:"contract_expiry"})}>Email</Btn>}
                    <Btn variant="smDanger" onClick={()=>del(c.id)}>Del</Btn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {modal&&(
        <Modal title={modal==="add"?"Add Contact":"Edit Contact"} onClose={closeModal}>
          <Field label="FIRST NAME"><input style={INP} value={form.firstName||""} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))}/></Field>
          <Field label="LAST NAME"><input style={INP} value={form.lastName||""} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))}/></Field>
          <Field label="EMAIL"><input style={INP} type="email" value={form.email||""} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></Field>
          <Field label="PHONE"><input style={INP} value={form.phone||""} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/></Field>
          <Field label="ROLE"><input style={INP} placeholder="e.g. Procurement, Finance" value={form.role||""} onChange={e=>setForm(p=>({...p,role:e.target.value}))}/></Field>
          <Field label="NOTES"><textarea style={TA} rows={2} value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></Field>
          <div style={{marginBottom:14}}><label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.text}}><input type="checkbox" checked={!!form.isPrimary} onChange={e=>setForm(p=>({...p,isPrimary:e.target.checked}))}/> Set as primary contact</label></div>
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={closeModal}>Cancel</Btn><Btn disabled={saving} onClick={save}>{saving?"Saving…":"Save"}</Btn></div>
        </Modal>
      )}

      {emailModal&&(
        <Modal title={`Email ${emailModal.contact.firstName}`} onClose={()=>setEmailModal(null)}>
          <Field label="EMAIL TYPE">
            <select style={SEL} value={emailModal.type} onChange={e=>setEmailModal(p=>({...p,type:e.target.value}))}>
              <option value="contract_expiry">Contract Expiry Reminder</option>
              <option value="sow_followup">SOW Follow-up</option>
              <option value="payment_reminder">Payment Reminder</option>
            </select>
          </Field>
          {emailModal.type==="contract_expiry"&&<><Field label="DOCUMENT NAME"><input style={INP} value={emailForm.docName} onChange={e=>setEmailForm(p=>({...p,docName:e.target.value}))}/></Field><Field label="EXPIRES ON"><input style={INP} type="date" value={emailForm.expiresAt} onChange={e=>setEmailForm(p=>({...p,expiresAt:e.target.value}))}/></Field></>}
          {emailModal.type==="sow_followup"&&<Field label="PROJECT NAME"><input style={INP} value={emailForm.projectName} onChange={e=>setEmailForm(p=>({...p,projectName:e.target.value}))}/></Field>}
          {emailModal.type==="payment_reminder"&&<><Field label="INVOICE #"><input style={INP} value={emailForm.invoiceNum} onChange={e=>setEmailForm(p=>({...p,invoiceNum:e.target.value}))}/></Field><Field label="AMOUNT (INR)"><input style={INP} type="number" value={emailForm.amount} onChange={e=>setEmailForm(p=>({...p,amount:e.target.value}))}/></Field><Field label="DUE DATE"><input style={INP} type="date" value={emailForm.dueAt} onChange={e=>setEmailForm(p=>({...p,dueAt:e.target.value}))}/></Field></>}
          <Field label="NOTE (OPTIONAL)"><textarea style={TA} rows={2} value={emailForm.adminNote} onChange={e=>setEmailForm(p=>({...p,adminNote:e.target.value}))}/></Field>
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setEmailModal(null)}>Cancel</Btn><Btn disabled={sending} onClick={sendEmail}>{sending?"Sending…":"Send Email"}</Btn></div>
        </Modal>
      )}
    </div>
  );
}

// ── Agreements Tab ────────────────────────────────────────────────────────────
function AgreementsTab({ customer, onRefresh }) {
  const [modal,   setModal]   = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [form,    setForm]    = useState({});
  const [file,    setFile]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState("");
  const filterType = f => t => f.type === t;

  // All docs: MSA/NDA at customer level + SOWs under projects
  const msaDocs  = (customer.documents||[]).filter(d=>d.type!=="sow");
  const sowDocs  = (customer.projects||[]).flatMap(p=>(p.documents||[]).map(d=>({...d,_projectName:p.name})));
  const allDocs  = [...msaDocs, ...sowDocs];

  async function save() {
    setSaving(true); setMsg("");
    try {
      if (editDoc) {
        await api("PATCH",`/customers/${customer.id}/documents/${editDoc.id}`,{ name:form.name, version:form.version, status:form.status, effectiveAt:form.effectiveAt||undefined, expiresAt:form.expiresAt||undefined, notes:form.notes });
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k,v])=>{ if(v) fd.append(k,v); });
        if (file) fd.append("file", file);
        await apiUpload(`/customers/${customer.id}/documents`, fd);
      }
      onRefresh(); setModal(false); setEditDoc(null); setForm({}); setFile(null);
    } catch(e){ setMsg(e.message); } finally { setSaving(false); }
  }

  async function del(docId) {
    if (!window.confirm("Delete this document?")) return;
    try { await api("DELETE",`/customers/${customer.id}/documents/${docId}`); onRefresh(); }
    catch(e){ alert(e.message); }
  }

  const statusColor = s=>({draft:"grey",active:"green",expired:"red",superseded:"yellow"}[s]||"grey");
  const typeColor   = t=>({msa:"purple",sow:"blue",nda:"yellow",other:"grey"}[t]||"grey");

  return (
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
        {msg&&<span style={{color:C.success,fontSize:13,marginRight:"auto"}}>{msg}</span>}
        <Btn variant="sm" onClick={()=>{setEditDoc(null);setForm({type:"msa",version:"v1.0",status:"draft"});setModal(true);}}>+ Upload Document</Btn>
      </div>
      {allDocs.length===0?<EmptyState message="No documents uploaded yet"/>:(
        <div className="cc-table-wrap" style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}>
          <table className="cc-table"><thead className="cc-thead"><tr className="cc-tr"><Th>Name</Th><Th>Type</Th><Th>Version</Th><Th>Project</Th><Th>Status</Th><Th>Effective</Th><Th>Expires</Th><Th>Actions</Th></tr></thead>
          <tbody className="cc-tbody">
            {allDocs.map(d=>(
              <tr key={d.id} className="cc-tr">
                <Td label="Name"><span style={{fontWeight:600}}>{d.name}</span></Td>
                <Td label="Type"><Badge label={d.type==="other"?(d.otherTypeName||"Other").toUpperCase():d.type.toUpperCase()} color={typeColor(d.type)}/></Td>
                <Td label="Version" style={{color:C.muted}}>{d.version}</Td>
                <Td label="Project" style={{color:C.muted}}>{d._projectName||"—"}</Td>
                <Td label="Status"><Badge label={d.status} color={statusColor(d.status)}/></Td>
                <Td label="Effective" style={{color:C.muted}}>{fmt(d.effectiveAt)}</Td>
                <Td label="Expires" style={{color:d.expiresAt&&new Date(d.expiresAt)<new Date()?C.danger:C.muted}}>{fmt(d.expiresAt)}</Td>
                <Td className="cc-td cc-td-actions">
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                    {d.fileUrl&&<a href={d.fileUrl} target="_blank" rel="noreferrer" style={{color:C.info,fontSize:12,fontWeight:600,textDecoration:"none"}}>↓ Download</a>}
                    <Btn variant="smGhost" onClick={()=>{setEditDoc(d);setForm({name:d.name,version:d.version,status:d.status,effectiveAt:d.effectiveAt?d.effectiveAt.slice(0,10):"",expiresAt:d.expiresAt?d.expiresAt.slice(0,10):"",notes:d.notes||""});setModal(true);}}>Edit</Btn>
                    <Btn variant="smDanger" onClick={()=>del(d.id)}>Del</Btn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {modal&&(
        <Modal title={editDoc?"Edit Document":"Upload Document"} onClose={()=>{setModal(false);setEditDoc(null);setForm({});setMsg("");}}>
          <Field label="DOCUMENT NAME"><input style={INP} value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></Field>
          {!editDoc&&<>
            <Field label="TYPE">
              <select style={SEL} value={form.type||"msa"} onChange={e=>setForm(p=>({...p,type:e.target.value,otherTypeName:undefined}))}>
                <option value="msa">MSA — Master Service Agreement</option>
                <option value="sow">SOW — Statement of Work</option>
                <option value="nda">NDA</option>
                <option value="other">Other — specify below</option>
              </select>
            </Field>
            {form.type==="other"&&<Field label="DOCUMENT TYPE NAME *">
              <input style={INP} placeholder="e.g. Addendum, LOI, MOU…" value={form.otherTypeName||""} onChange={e=>setForm(p=>({...p,otherTypeName:e.target.value}))}/>
            </Field>}
            {form.type==="sow"&&<Field label="PROJECT">
              <select style={SEL} value={form.projectId||""} onChange={e=>setForm(p=>({...p,projectId:e.target.value}))}>
                <option value="">— Select project —</option>
                {(customer.projects||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>}
          </>}
          <Field label="VERSION"><input style={INP} value={form.version||""} onChange={e=>setForm(p=>({...p,version:e.target.value}))}/></Field>
          <Field label="STATUS">
            <select style={SEL} value={form.status||"draft"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
              <option value="draft">Draft</option><option value="active">Active</option>
              <option value="expired">Expired</option><option value="superseded">Superseded</option>
            </select>
          </Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="EFFECTIVE DATE"><input style={INP} type="date" value={form.effectiveAt||""} onChange={e=>setForm(p=>({...p,effectiveAt:e.target.value}))}/></Field>
            <Field label="EXPIRY DATE"><input style={INP} type="date" value={form.expiresAt||""} onChange={e=>setForm(p=>({...p,expiresAt:e.target.value}))}/></Field>
          </div>
          <Field label="NOTES"><textarea style={TA} rows={2} value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></Field>
          {!editDoc&&<Field label="FILE (PDF / DOCX, max 20MB)"><input type="file" accept=".pdf,.doc,.docx" onChange={e=>setFile(e.target.files[0])} style={{color:C.text,fontSize:13}}/></Field>}
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>{setModal(false);setEditDoc(null);setForm({});setMsg("");}}>Cancel</Btn><Btn disabled={saving} onClick={save}>{saving?"Saving…":"Save"}</Btn></div>
        </Modal>
      )}
    </div>
  );
}

// ── ProjectsTab ───────────────────────────────────────────────────────────────
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const MODALITY_ICON = { video:"🎥", voice:"🎙", chat:"💬" };

function ProjectsTab({ customer, onRefresh, onNewSurvey }) {
  const cid      = customer.id;
  const projects = customer.projects || [];
  const [modal,         setModal]         = useState(false);
  const [editProj,      setEditProj]      = useState(null);
  const [form,          setForm]          = useState({});
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [payModal,      setPayModal]      = useState(null);   // invoice object
  const [payForm,       setPayForm]       = useState({});
  const [payMsg,        setPayMsg]        = useState("");
  const [payEmailModal, setPayEmailModal] = useState(null);   // { invoiceNum, emailData }

  const INP = {width:"100%",padding:"8px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"};
  const SEL = {...INP,cursor:"pointer"};
  const TA  = {...INP,resize:"vertical"};

  function openNew()   { setEditProj(null); setForm({status:"active"}); setMsg(""); setModal(true); }
  function openEdit(p) { setEditProj(p); setForm({name:p.name,status:p.status,description:p.description||"",startDate:p.startDate?p.startDate.slice(0,10):"",endDate:p.endDate?p.endDate.slice(0,10):""}); setMsg(""); setModal(true); }

  async function save() {
    if (!form.name?.trim()) { setMsg("Project name is required."); return; }
    setSaving(true); setMsg("");
    try {
      if (editProj) await api("PATCH", `/customers/${cid}/projects/${editProj.id}`, form);
      else          await api("POST",  `/customers/${cid}/projects`, form);
      setModal(false); setEditProj(null); setForm({}); onRefresh();
    } catch(e) { setMsg(e.message); } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api("DELETE", `/surveys/${confirmDelete.id}`);
      setConfirmDelete(null);
      onRefresh();
    } catch(e) { alert(`Failed to delete: ${e.message}`); }
    finally { setDeleting(false); }
  }

  async function recordPayment() {
    if (!payForm.amount) { setPayMsg("Amount is required."); return; }
    setSaving(true); setPayMsg("");
    try {
      await api("POST", `/customers/${cid}/invoices/${payModal.id}/payments`, payForm);
      setPayModal(null); setPayForm({}); onRefresh();
    } catch(e) { setPayMsg(e.message||"Failed to record payment."); } finally { setSaving(false); }
  }

  async function sendPaymentReminder() {
    setSaving(true);
    try {
      await api("POST", `/customers/${cid}/email`, {
        type:"payment_reminder", adminNote: payEmailModal.invoiceNum, ...payEmailModal.emailData,
      });
      setPayEmailModal(null);
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  }

  const statusColor    = s => s==="active"?"green":s==="completed"?"blue":s==="on_hold"?"yellow":"grey";
  const sowStatusColor = s => s==="active"?"green":s==="expired"?"red":s==="draft"?"grey":"yellow";
  const invStatusColor = s => s==="paid"?"green":s==="overdue"?"red":s==="sent"?"blue":s==="partial"?"yellow":"grey";

  return (
    <div>
      {/* Delete draft survey confirm */}
      {confirmDelete && (
        <div style={{position:"fixed",inset:0,background:"#000a",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:C.card,border:`1px solid ${C.danger}`,borderRadius:12,padding:28,maxWidth:400,width:"90%"}}>
            <div style={{fontWeight:700,fontSize:15,color:C.text,marginBottom:8}}>Delete Draft Survey?</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:20}}>
              "<span style={{color:C.text,fontWeight:600}}>{confirmDelete.title}</span>" will be permanently deleted.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>setConfirmDelete(null)} disabled={deleting}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDelete} disabled={deleting}>{deleting?"Deleting…":"Delete"}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {payModal&&(
        <Modal title={`Record Payment — ${payModal.invoiceNum}`} onClose={()=>{setPayModal(null);setPayForm({});setPayMsg("");}}>
          <Field label="AMOUNT *"><input style={INP} type="number" min="1" step="0.01" value={payForm.amount||""} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}/></Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="PAYMENT DATE"><input style={INP} type="date" value={payForm.paidAt||""} onChange={e=>setPayForm(p=>({...p,paidAt:e.target.value}))}/></Field>
            <Field label="METHOD">
              <select style={SEL} value={payForm.method||"bank_transfer"} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
                <option value="bank_transfer">Bank Transfer</option><option value="upi">UPI</option>
                <option value="cheque">Cheque</option><option value="credit_card">Credit Card</option><option value="other">Other</option>
              </select>
            </Field>
          </div>
          <Field label="REFERENCE / UTR"><input style={INP} value={payForm.reference||""} onChange={e=>setPayForm(p=>({...p,reference:e.target.value}))}/></Field>
          <Field label="NOTES"><textarea style={TA} rows={2} value={payForm.notes||""} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))}/></Field>
          {payMsg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{payMsg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>{setPayModal(null);setPayForm({});setPayMsg("");}}>Cancel</Btn>
            <Btn variant="success" disabled={saving} onClick={recordPayment}>{saving?"Saving…":"Record Payment"}</Btn>
          </div>
        </Modal>
      )}

      {/* Payment Reminder Email Modal */}
      {payEmailModal&&(
        <Modal title="Send Payment Reminder" onClose={()=>setPayEmailModal(null)}>
          <p style={{color:C.muted,fontSize:13,marginBottom:8}}>Send a payment reminder to {customer.companyName} for invoice <strong style={{color:C.accent}}>{payEmailModal.invoiceNum}</strong>.</p>
          <p style={{color:C.text,fontSize:13}}>{payEmailModal.emailData?.adminNote}</p>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setPayEmailModal(null)}>Cancel</Btn>
            <Btn disabled={saving} onClick={sendPaymentReminder}>{saving?"Sending…":"Send Reminder"}</Btn>
          </div>
        </Modal>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontWeight:600,color:C.text}}>Projects ({projects.length})</span>
        <Btn variant="sm" onClick={openNew}>+ New Project</Btn>
      </div>

      {projects.length===0 ? <EmptyState message="No projects yet."/> : (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {projects.map(p=>{
            const sows     = (p.documents||[]).filter(d=>d.type==="sow");
            const surveys  = (p.surveys||[]);
            const invoices = (customer.invoices||[]).filter(i=>i.projectId===p.id);
            return (
              <div key={p.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>

                {/* ── Card header ── */}
                <div style={{padding:"14px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,color:C.text,fontSize:15}}>{p.name}</span>
                      <Badge label={slug(p.status)} color={statusColor(p.status)}/>
                    </div>
                    {p.description&&<div style={{color:C.muted,fontSize:12,marginTop:4}}>{p.description}</div>}
                    <div style={{display:"flex",gap:20,flexWrap:"wrap",marginTop:6}}>
                      {p.startDate&&<span style={{fontSize:12,color:C.muted}}>Start: <span style={{color:C.text}}>{fmt(p.startDate)}</span></span>}
                      {p.endDate&&<span style={{fontSize:12,color:C.muted}}>End: <span style={{color:C.text}}>{fmt(p.endDate)}</span></span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0,marginLeft:12}}>
                    <Btn variant="sm" onClick={()=>onNewSurvey&&onNewSurvey(p.id)}>+ New Survey</Btn>
                    <Btn variant="smGhost" onClick={()=>openEdit(p)}>Edit</Btn>
                  </div>
                </div>

                {/* ── SOW section ── */}
                {sows.length>0&&(
                  <div style={{padding:"10px 16px",borderBottom:`1px solid ${C.border}`,background:C.surface}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Statement of Work</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {sows.map(s=>(
                        <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                          <span style={{fontSize:13,color:C.text,fontWeight:600}}>{s.name}</span>
                          {s.version&&<span style={{fontSize:11,color:C.muted}}>v{s.version}</span>}
                          <Badge label={s.status} color={sowStatusColor(s.status)}/>
                          {s.effectiveAt&&<span style={{fontSize:11,color:C.muted}}>Effective: <span style={{color:C.text}}>{fmt(s.effectiveAt)}</span></span>}
                          {s.expiresAt&&<span style={{fontSize:11,color:new Date(s.expiresAt)<new Date()?C.danger:C.muted}}>
                            Expires: <span style={{color:new Date(s.expiresAt)<new Date()?C.danger:C.text}}>{fmt(s.expiresAt)}</span>
                          </span>}
                          {s.fileUrl&&<a href={s.fileUrl} target="_blank" rel="noreferrer" style={{fontSize:11,color:C.accent,textDecoration:"none"}}>↗ View</a>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Invoices section ── */}
                {invoices.length>0&&(
                  <div style={{padding:"10px 16px",borderBottom:`1px solid ${C.border}`}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Invoices</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {invoices.map(inv=>{
                        const tot  = inv.totalAmount||inv.total||0;
                        const paid = inv.paidAmount||0;
                        const over = inv.dueAt&&new Date(inv.dueAt)<new Date()&&inv.status!=="paid";
                        return (
                          <div key={inv.id} style={{display:"flex",alignItems:"center",gap:10,background:C.surface,borderRadius:7,padding:"8px 12px",flexWrap:"wrap",border:`1px solid ${C.border}`}}>
                            <span style={{fontWeight:700,fontSize:12,color:C.accent,flexShrink:0}}>{inv.invoiceNum}</span>
                            {inv.description&&<span style={{fontSize:12,color:C.text,flex:1,minWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.description}</span>}
                            <span style={{fontSize:12,fontWeight:600,color:C.text,flexShrink:0}}>₹{inr(tot)}</span>
                            {paid>0&&paid<tot&&<span style={{fontSize:11,color:C.muted,flexShrink:0}}>Paid ₹{inr(paid)}</span>}
                            <Badge label={slug(inv.status)} color={invStatusColor(inv.status)}/>
                            {inv.dueAt&&<span style={{fontSize:11,color:over?C.danger:C.muted,flexShrink:0}}>Due {fmt(inv.dueAt)}</span>}
                            <div style={{display:"flex",gap:5,marginLeft:"auto",flexShrink:0}}>
                              {inv.status!=="paid"&&<Btn variant="smSuccess" onClick={()=>{setPayModal(inv);setPayForm({currency:inv.currency||"INR"});setPayMsg("");}}>+ Pay</Btn>}
                              <Btn variant="smInfo" onClick={()=>setPayEmailModal({invoiceNum:inv.invoiceNum,emailData:{adminNote:`Invoice ${inv.invoiceNum} — ${inv.currency||"INR"} ${inr(tot)} due ${fmt(inv.dueAt)}.`}})}>Email</Btn>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Surveys section ── */}
                <div style={{padding:"10px 16px"}}>
                  {surveys.length>0 ? (
                    <>
                      <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Surveys</div>
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {surveys.map(s=>{
                          const isDraft = s.status==="draft";
                          const qCount  = Array.isArray(s.questions) ? s.questions.length : 0;
                          return (
                            <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,background:C.surface,borderRadius:7,padding:"8px 12px",cursor:"pointer",border:`1px solid ${C.border}`}}
                              onClick={()=>onNewSurvey&&onNewSurvey(s.projectId,s)}>
                              <span style={{fontSize:15,lineHeight:1}}>{MODALITY_ICON[s.modality]||"📋"}</span>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontWeight:600,fontSize:13,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.title}</div>
                                <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                                  {qCount} question{qCount!==1?"s":""} · {s.modality}
                                  {s.estimatedMins ? ` · ${s.estimatedMins} min` : ""}
                                </div>
                              </div>
                              <Badge label={s.status} color={s.status==="active"?"green":s.status==="closed"?"red":"grey"}/>
                              {isDraft&&(
                                <button onClick={e=>{e.stopPropagation();setConfirmDelete(s);}} title="Delete draft"
                                  style={{background:"transparent",border:"none",cursor:"pointer",color:C.danger,display:"flex",alignItems:"center",padding:"4px",borderRadius:4,opacity:0.7}}
                                  onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                                  onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
                                  <IconTrash/>
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div style={{fontSize:11,color:C.muted}}>No surveys yet — click "+ New Survey" to create one.</div>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}

      {modal&&(
        <Modal title={editProj?"Edit Project":"New Project"} onClose={()=>{setModal(false);setForm({});}}>
          <Field label="PROJECT NAME *"><input style={INP} value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></Field>
          <Field label="STATUS">
            <select style={SEL} value={form.status||"active"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
              <option value="active">Active</option><option value="on_hold">On Hold</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="START DATE"><input style={INP} type="date" value={form.startDate||""} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></Field>
            <Field label="END DATE"><input style={INP} type="date" value={form.endDate||""} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></Field>
          </div>
          <Field label="DESCRIPTION"><textarea style={TA} rows={3} value={form.description||""} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/></Field>
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>{setModal(false);setForm({});}}>Cancel</Btn><Btn disabled={saving} onClick={save}>{saving?"Saving…":"Save"}</Btn></div>
        </Modal>
      )}
    </div>
  );
}

// ── InvoicesTab ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 25;
function InvoicesTab({ customer, onRefresh }) {
  const cid      = customer.id;
  const invoices = customer.invoices || [];
  const projects = customer.projects || [];
  const [modal,      setModal]      = useState(false);
  const [editInv,    setEditInv]    = useState(null);
  const [payModal,   setPayModal]   = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [form,       setForm]       = useState({});
  const [payForm,    setPayForm]    = useState({});
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState("");

  // Filters + pagination
  const [filterProject, setFilterProject] = useState("");
  const [filterStatus,  setFilterStatus]  = useState("");
  const [filterDue,     setFilterDue]     = useState("");  // "" | "overdue" | "month" | "next30"
  const [page,          setPage]          = useState(1);

  const INP  = {width:"100%",padding:"8px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"};
  const INR0 = {...INP,background:C.bg,color:C.muted,cursor:"not-allowed"};
  const SEL  = {...INP,cursor:"pointer"};
  const TA   = {...INP,resize:"vertical"};
  const SEC  = {marginTop:16,paddingTop:14,borderTop:`1px solid ${C.border}`};
  const SH   = {fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8};

  const statusColor = s => s==="paid"?"green":s==="overdue"?"red":s==="sent"?"blue":s==="partial"?"yellow":"grey";
  const projectMap  = Object.fromEntries(projects.map(p=>[p.id, p.name]));

  const DEFAULT_FORM = {
    description:"", currency:"INR", taxRate:"18", gstEnabled: true,
    projectId:"",
    lineItems:[{description:"",quantity:1,unitPrice:""}],
    discountDescription:"", discountType:"percentage", discountValue:"",
    billingPeriodFrom:"", billingPeriodTo:"",
    gstin: customer.gstin||"", placeOfSupply:"",
    dueAt:"", notes:"",
  };

  function openNew()  { setEditInv(null); setForm({...DEFAULT_FORM}); setMsg(""); setModal(true); }
  function openEdit(inv) {
    setEditInv(inv);
    setForm({
      description:        inv.description        || "",
      currency:           inv.currency           || "INR",
      status:             inv.status             || "draft",
      taxRate:            String(inv.taxRate      ?? 18),
      gstEnabled:         (inv.taxRate ?? 18) > 0,
      projectId:          inv.projectId           || "",
      lineItems:          inv.lineItems ? (typeof inv.lineItems==="string" ? JSON.parse(inv.lineItems) : inv.lineItems) : [{description:"",quantity:1,unitPrice:""}],
      discountDescription:inv.discountDescription|| "",
      discountType:       inv.discountType        || "percentage",
      discountValue:      inv.discountValue != null ? String(inv.discountValue) : "",
      billingPeriodFrom:  inv.billingPeriodFrom   ? inv.billingPeriodFrom.slice(0,10) : "",
      billingPeriodTo:    inv.billingPeriodTo     ? inv.billingPeriodTo.slice(0,10)   : "",
      gstin:              inv.gstin               || customer.gstin || "",
      placeOfSupply:      inv.placeOfSupply       || "",
      dueAt:              inv.dueAt               ? inv.dueAt.slice(0,10) : "",
      notes:              inv.notes               || "",
    });
    setMsg(""); setModal(true);
  }

  function setLine(i, field, val) {
    setForm(f=>{ const li=[...f.lineItems]; li[i]={...li[i],[field]:val}; return {...f,lineItems:li}; });
  }
  function addLine()     { setForm(f=>({...f,lineItems:[...f.lineItems,{description:"",quantity:1,unitPrice:""}]})); }
  function removeLine(i) { setForm(f=>({...f,lineItems:f.lineItems.filter((_,j)=>j!==i)})); }

  // Live preview (mirrors server-side computeInvoiceTotals)
  const previewTotals = (() => {
    const items    = form.lineItems || [];
    const subtotal = items.reduce((s,l)=>s+(parseFloat(l.quantity||1)*parseFloat(l.unitPrice||0)),0);
    let discountAmount = 0;
    if (form.discountValue && parseFloat(form.discountValue)>0) {
      discountAmount = form.discountType==="percentage"
        ? subtotal * parseFloat(form.discountValue) / 100
        : parseFloat(form.discountValue);
    }
    discountAmount   = Math.min(discountAmount, subtotal);
    const taxableVal = subtotal - discountAmount;
    const gstRate    = form.gstEnabled ? Math.max(0, parseFloat(form.taxRate)||0) : 0;
    const taxAmount  = taxableVal * gstRate / 100;
    const cgstRate   = gstRate / 2;
    return {
      subtotal:       Math.round(subtotal      *100)/100,
      discountAmount: Math.round(discountAmount*100)/100,
      taxableVal:     Math.round(taxableVal    *100)/100,
      cgst:           Math.round(taxableVal*cgstRate/100*100)/100,
      sgst:           Math.round(taxableVal*cgstRate/100*100)/100,
      taxAmount:      Math.round(taxAmount     *100)/100,
      total:          Math.round((taxableVal+taxAmount)*100)/100,
      cgstRate, sgstRate: cgstRate,
    };
  })();

  const cur  = form.currency || "INR";
  const fmt2 = n => n.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

  async function save() {
    if (!form.lineItems?.length) { setMsg("Add at least one line item."); return; }
    setSaving(true); setMsg("");
    try {
      const payload = {
        description:         form.description         || null,
        projectId:           form.projectId           || null,
        currency:            form.currency            || "INR",
        lineItems:           form.lineItems,
        discountDescription: form.discountDescription || null,
        discountType:        form.discountValue ? (form.discountType || "percentage") : null,
        discountValue:       form.discountValue ? parseFloat(form.discountValue) : null,
        taxRate:             form.gstEnabled ? (parseFloat(form.taxRate) || 18) : 0,
        dueAt:               form.dueAt               || null,
        billingPeriodFrom:   form.billingPeriodFrom    || null,
        billingPeriodTo:     form.billingPeriodTo      || null,
        gstin:               form.gstin               || null,
        placeOfSupply:       form.placeOfSupply        || null,
        notes:               form.notes               || null,
        ...(editInv && form.status ? { status: form.status } : {}),
      };
      if (editInv) await api("PATCH", `/customers/${cid}/invoices/${editInv.id}`, payload);
      else         await api("POST",  `/customers/${cid}/invoices`, payload);
      setModal(false); setEditInv(null); setForm({}); onRefresh();
    } catch(e) { setMsg(e.message||"Failed to save invoice."); }
    finally { setSaving(false); }
  }

  async function recordPayment() {
    if (!payForm.amount) { setMsg("Amount is required."); return; }
    setSaving(true); setMsg("");
    try {
      await api("POST", `/customers/${cid}/invoices/${payModal.id}/payments`, payForm);
      setPayModal(null); setPayForm({}); onRefresh();
    } catch(e) { setMsg(e.message); } finally { setSaving(false); }
  }

  async function sendInvoiceEmail() {
    setSaving(true);
    try {
      await api("POST", `/customers/${cid}/email`, {
        type:"payment_reminder", adminNote: emailModal.invoiceNum, ...emailModal.emailData,
      });
      setEmailModal(null);
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  }

  // Summary totals (across all invoices, not filtered)
  const totalInvoiced  = invoices.reduce((s,i)=>s+Number(i.totalAmount||i.total||0),0);
  const totalCollected = invoices.reduce((s,i)=>s+Number(i.paidAmount||0),0);
  const totalOpen      = totalInvoiced - totalCollected;

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const now = new Date();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth()+1, 0, 23, 59, 59);
  const next30     = new Date(now.getTime() + 30*24*60*60*1000);

  const filtered = invoices
    .filter(inv => {
      if (filterProject === "__none__" && inv.projectId) return false;
      if (filterProject && filterProject !== "__none__" && inv.projectId !== filterProject) return false;
      if (filterStatus  && inv.status    !== filterStatus)  return false;
      if (filterDue) {
        const d = inv.dueAt ? new Date(inv.dueAt) : null;
        if (filterDue === "overdue")  return d && d < now && inv.status !== "paid";
        if (filterDue === "month")    return d && d <= endOfMonth && d >= now;
        if (filterDue === "next30")   return d && d <= next30 && d >= now;
        if (filterDue === "noduedate") return !d;
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by dueAt ascending; nulls at end
      if (!a.dueAt && !b.dueAt) return 0;
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt) - new Date(b.dueAt);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage-1)*PAGE_SIZE, safePage*PAGE_SIZE);

  function resetPage() { setPage(1); }

  const FSEL = {padding:"6px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:12,fontFamily:"inherit",cursor:"pointer"};

  return (
    <div>
      {/* Summary cards */}
      <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
        {[["Total Invoiced",inr(totalInvoiced),C.text],["Collected",inr(totalCollected),C.success],["Outstanding",inr(totalOpen),totalOpen>0?C.danger:C.muted]].map(([l,v,c])=>(
          <div key={l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 20px",flex:"1 1 120px"}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{l}</div>
            <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Filters + New button row */}
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        {projects.length>0&&(
          <select style={FSEL} value={filterProject} onChange={e=>{setFilterProject(e.target.value);resetPage();}}>
            <option value="">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            <option value="__none__">No Project</option>
          </select>
        )}
        <select style={FSEL} value={filterStatus} onChange={e=>{setFilterStatus(e.target.value);resetPage();}}>
          <option value="">All Statuses</option>
          {["draft","sent","partial","paid","overdue","cancelled"].map(s=>(
            <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
          ))}
        </select>
        <select style={FSEL} value={filterDue} onChange={e=>{setFilterDue(e.target.value);resetPage();}}>
          <option value="">All Due Dates</option>
          <option value="overdue">Overdue</option>
          <option value="month">Due this month</option>
          <option value="next30">Due in 30 days</option>
          <option value="noduedate">No due date</option>
        </select>
        {(filterProject||filterStatus||filterDue)&&(
          <button style={{background:"transparent",border:"none",color:C.muted,fontSize:12,cursor:"pointer",padding:"4px 6px"}}
            onClick={()=>{setFilterProject("");setFilterStatus("");setFilterDue("");resetPage();}}>✕ Clear</button>
        )}
        <div style={{marginLeft:"auto"}}><Btn variant="sm" onClick={openNew}>+ New Invoice</Btn></div>
      </div>

      {/* Invoice list table */}
      {filtered.length===0 ? <EmptyState message={invoices.length===0?"No invoices yet.":"No invoices match the current filters."}/> : (
        <>
          <div className="cc-table-wrap" style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}>
            <table className="cc-table"><thead className="cc-thead"><tr className="cc-tr">
              {["Invoice #","Project","Description","Total Payable","Paid","Status","Due ↑",""].map(h=><Th key={h}>{h}</Th>)}
            </tr></thead>
            <tbody className="cc-tbody">
              {pageRows.map(inv=>{
                const tot  = inv.totalAmount || inv.total || 0;
                const paid = inv.paidAmount  || 0;
                const over = inv.dueAt && new Date(inv.dueAt)<now && inv.status!=="paid";
                const projName = inv.projectId ? (projectMap[inv.projectId]||"—") : "—";
                return (
                  <tr key={inv.id} className="cc-tr">
                    <Td label="Invoice #"><span style={{color:C.accent,fontWeight:700,fontSize:12}}>{inv.invoiceNum}</span></Td>
                    <Td label="Project"><span style={{fontSize:12,color:projName==="—"?C.muted:C.text}}>{projName}</span></Td>
                    <Td label="Description">{inv.description||"—"}</Td>
                    <Td label="Total Payable"><span style={{fontWeight:600}}>{inv.currency} {inr(tot)}</span></Td>
                    <Td label="Paid" style={{color:paid>=tot&&tot>0?C.success:C.muted}}>{inr(paid)}</Td>
                    <Td label="Status"><Badge label={slug(inv.status)} color={statusColor(inv.status)}/></Td>
                    <Td label="Due" style={{color:over?C.danger:C.muted,fontSize:12}}>{fmt(inv.dueAt)}</Td>
                    <Td className="cc-td cc-td-actions">
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        <Btn variant="smGhost" onClick={()=>openEdit(inv)}>Edit</Btn>
                        {inv.status!=="paid"&&<Btn variant="smSuccess" onClick={()=>{setPayModal(inv);setPayForm({currency:inv.currency||"INR"});setMsg("");}}>+ Pay</Btn>}
                        <Btn variant="smInfo" onClick={()=>setEmailModal({id:inv.id,invoiceNum:inv.invoiceNum,emailData:{adminNote:`Invoice ${inv.invoiceNum} (${inv.currency} ${inr(tot)}) due ${fmt(inv.dueAt)}.`}})}>Email</Btn>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:12,fontSize:13,color:C.muted}}>
              <span>{filtered.length} invoices · page {safePage} of {totalPages}</span>
              <div style={{display:"flex",gap:6}}>
                <Btn variant="smGhost" disabled={safePage===1}          onClick={()=>setPage(1)}>«</Btn>
                <Btn variant="smGhost" disabled={safePage===1}          onClick={()=>setPage(p=>p-1)}>‹ Prev</Btn>
                <Btn variant="smGhost" disabled={safePage===totalPages} onClick={()=>setPage(p=>p+1)}>Next ›</Btn>
                <Btn variant="smGhost" disabled={safePage===totalPages} onClick={()=>setPage(totalPages)}>»</Btn>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Create / Edit Invoice Modal ── */}
      {modal&&(
        <Modal title={editInv?`Edit ${editInv.invoiceNum}`:"New Invoice"} onClose={()=>{setModal(false);setForm({});}} style={{maxWidth:680}}>

          {/* ① Invoice header */}
          <div style={SEC}>
            <div style={SH}>Invoice Details</div>
            {editInv&&(
              <div style={{marginBottom:10,padding:"6px 10px",borderRadius:6,background:C.bg,fontSize:12,color:C.muted}}>
                Invoice No: <strong style={{color:C.accent}}>{editInv.invoiceNum}</strong>
                &nbsp;·&nbsp;Customer: <strong style={{color:C.text}}>{customer.companyName}</strong>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <Field label="BILLING PERIOD FROM"><input style={INP} type="date" value={form.billingPeriodFrom||""} onChange={e=>setForm(p=>({...p,billingPeriodFrom:e.target.value}))}/></Field>
              <Field label="BILLING PERIOD TO">  <input style={INP} type="date" value={form.billingPeriodTo  ||""} onChange={e=>setForm(p=>({...p,billingPeriodTo:  e.target.value}))}/></Field>
              <Field label="GSTIN"><input style={INP} placeholder="e.g. 29AABCT1332L1ZN" value={form.gstin||""} onChange={e=>setForm(p=>({...p,gstin:e.target.value}))}/></Field>
              <Field label="PLACE OF SUPPLY"><input style={INP} placeholder="e.g. Karnataka" value={form.placeOfSupply||""} onChange={e=>setForm(p=>({...p,placeOfSupply:e.target.value}))}/></Field>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:6}}>
              <Field label="PROJECT (optional)">
                <select style={SEL} value={form.projectId||""} onChange={e=>setForm(p=>({...p,projectId:e.target.value}))}>
                  <option value="">— No project —</option>
                  {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="DUE DATE"><input style={INP} type="date" value={form.dueAt||""} onChange={e=>setForm(p=>({...p,dueAt:e.target.value}))}/></Field>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr"+(editInv?" 1fr":""),gap:10,marginTop:6}}>
              <Field label="CURRENCY">
                <select style={SEL} value={form.currency||"INR"} onChange={e=>setForm(p=>({...p,currency:e.target.value}))}>
                  <option value="INR">INR ₹</option><option value="USD">USD $</option><option value="EUR">EUR €</option>
                </select>
              </Field>
              {editInv&&<Field label="STATUS">
                <select style={SEL} value={form.status||"draft"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                  <option value="draft">Draft</option><option value="sent">Sent</option>
                  <option value="partial">Partial</option><option value="paid">Paid</option><option value="overdue">Overdue</option>
                </select>
              </Field>}
            </div>
            <Field label="DESCRIPTION / TITLE" style={{marginTop:6}}>
              <input style={INP} placeholder="e.g. Research Services — Q1 2025" value={form.description||""} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
            </Field>
          </div>

          {/* ② Line items */}
          <div style={SEC}>
            <div style={SH}>Line Items</div>
            <div style={{display:"grid",gridTemplateColumns:"3fr 55px 95px 90px 24px",gap:6,marginBottom:4}}>
              {["Description","Qty","Unit Price","Amount",""].map(h=>(
                <div key={h} style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}>{h}</div>
              ))}
            </div>
            {(form.lineItems||[]).map((li,i)=>{
              const amt = parseFloat(li.quantity||1)*parseFloat(li.unitPrice||0);
              return (
                <div key={i} style={{display:"grid",gridTemplateColumns:"3fr 55px 95px 90px 24px",gap:6,marginBottom:6,alignItems:"center"}}>
                  <input style={INP} placeholder="e.g. Platform access fee" value={li.description||""} onChange={e=>setLine(i,"description",e.target.value)}/>
                  <input style={INP} type="number" min="1" value={li.quantity||""} onChange={e=>setLine(i,"quantity",e.target.value)}/>
                  <input style={INP} type="number" min="0" step="0.01" value={li.unitPrice||""} onChange={e=>setLine(i,"unitPrice",e.target.value)}/>
                  <input style={INR0} readOnly value={amt>0?fmt2(amt):""}/>
                  <button onClick={()=>removeLine(i)} style={{background:"transparent",border:"none",color:C.danger,cursor:"pointer",fontSize:18,lineHeight:1,padding:0}}>×</button>
                </div>
              );
            })}
            <Btn variant="smGhost" onClick={addLine}>+ Add line</Btn>
          </div>

          {/* ③ Discount */}
          <div style={SEC}>
            <div style={SH}>Discount <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 130px 110px",gap:10}}>
              <Field label="DESCRIPTION"><input style={INP} placeholder="e.g. Early payment discount" value={form.discountDescription||""} onChange={e=>setForm(p=>({...p,discountDescription:e.target.value}))}/></Field>
              <Field label="TYPE">
                <select style={SEL} value={form.discountType||"percentage"} onChange={e=>setForm(p=>({...p,discountType:e.target.value}))}>
                  <option value="percentage">Percentage %</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </Field>
              <Field label={form.discountType==="flat"?`VALUE (${cur})`:"VALUE (%)"}>
                <input style={INP} type="number" min="0" step="0.01" placeholder={form.discountType==="flat"?"500":"10"} value={form.discountValue||""} onChange={e=>setForm(p=>({...p,discountValue:e.target.value}))}/>
              </Field>
            </div>
          </div>

          {/* ④ GST */}
          <div style={SEC}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={SH}>GST</span>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                <span style={{fontSize:12,color:form.gstEnabled?C.text:C.muted}}>{form.gstEnabled?"Enabled":"Disabled"}</span>
                <div style={{position:"relative",width:36,height:20}} onClick={()=>setForm(p=>({...p,gstEnabled:!p.gstEnabled}))}>
                  <div style={{position:"absolute",inset:0,borderRadius:10,background:form.gstEnabled?C.accent:"#444",transition:"background 0.2s"}}/>
                  <div style={{position:"absolute",top:2,left:form.gstEnabled?18:2,width:16,height:16,borderRadius:8,background:"#fff",transition:"left 0.2s"}}/>
                </div>
              </label>
            </div>
            {form.gstEnabled ? (
              <div style={{display:"grid",gridTemplateColumns:"100px 1fr 1fr 1fr",gap:10,alignItems:"end"}}>
                <Field label="GST RATE %">
                  <input style={INP} type="number" min="0" max="100" step="0.01" value={form.taxRate||""} onChange={e=>setForm(p=>({...p,taxRate:e.target.value}))}/>
                </Field>
                <Field label={`CGST @ ${previewTotals.cgstRate}%`}>
                  <input style={INR0} readOnly value={previewTotals.taxableVal>0?fmt2(previewTotals.cgst):""}/>
                </Field>
                <Field label={`SGST @ ${previewTotals.sgstRate}%`}>
                  <input style={INR0} readOnly value={previewTotals.taxableVal>0?fmt2(previewTotals.sgst):""}/>
                </Field>
                <Field label="TOTAL GST">
                  <input style={INR0} readOnly value={previewTotals.taxableVal>0?fmt2(previewTotals.taxAmount):""}/>
                </Field>
              </div>
            ) : (
              <div style={{fontSize:12,color:C.muted,padding:"8px 0"}}>GST is disabled — no tax will be applied to this invoice.</div>
            )}
          </div>

          {/* ⑤ Summary */}
          <div style={{...SEC,background:C.surface,borderRadius:8,padding:"14px 16px",marginTop:16}}>
            {[
              ["Subtotal (before discount & tax)", previewTotals.subtotal,       C.muted,  false],
              ["Discount",                         previewTotals.discountAmount,  C.danger, false],
              ...(form.gstEnabled ? [["Tax (CGST + SGST)", previewTotals.taxAmount, C.muted, false]] : []),
            ].map(([label, value, color, bold])=>(
              <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12,color}}>
                <span>{label}</span><span style={{fontWeight:bold?700:500}}>{cur} {fmt2(value)}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 4px",fontSize:14,fontWeight:700,color:C.text,borderTop:`1px solid ${C.border}`,marginTop:6}}>
              <span>Total Payable</span><span style={{color:C.accent}}>{cur} {fmt2(previewTotals.total)}</span>
            </div>
          </div>

          {/* ⑥ Value Summary */}
          <div style={SEC}>
            <Field label="VALUE SUMMARY">
              <textarea style={TA} rows={3} placeholder="Payment terms, bank details, or other notes…" value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
            </Field>
          </div>

          {msg&&<div style={{color:C.danger,fontSize:13,marginTop:10,marginBottom:4}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>{setModal(false);setForm({});}}>Cancel</Btn>
            <Btn disabled={saving} onClick={save}>{saving?"Saving…":"Save Invoice"}</Btn>
          </div>
        </Modal>
      )}

      {/* Record Payment Modal */}
      {payModal&&(
        <Modal title={`Record Payment — ${payModal.invoiceNum}`} onClose={()=>{setPayModal(null);setPayForm({});setMsg("");}}>
          <Field label="AMOUNT *"><input style={INP} type="number" min="1" step="0.01" value={payForm.amount||""} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}/></Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="PAYMENT DATE"><input style={INP} type="date" value={payForm.paidAt||""} onChange={e=>setPayForm(p=>({...p,paidAt:e.target.value}))}/></Field>
            <Field label="METHOD">
              <select style={SEL} value={payForm.method||"bank_transfer"} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
                <option value="bank_transfer">Bank Transfer</option><option value="upi">UPI</option>
                <option value="cheque">Cheque</option><option value="credit_card">Credit Card</option><option value="other">Other</option>
              </select>
            </Field>
          </div>
          <Field label="REFERENCE / UTR"><input style={INP} value={payForm.reference||""} onChange={e=>setPayForm(p=>({...p,reference:e.target.value}))}/></Field>
          <Field label="NOTES"><textarea style={TA} rows={2} value={payForm.notes||""} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))}/></Field>
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>{setPayModal(null);setPayForm({});setMsg("");}}>Cancel</Btn>
            <Btn variant="success" disabled={saving} onClick={recordPayment}>{saving?"Saving…":"Record Payment"}</Btn>
          </div>
        </Modal>
      )}

      {/* Payment Reminder Email Modal */}
      {emailModal&&(
        <Modal title="Send Payment Reminder" onClose={()=>setEmailModal(null)}>
          <p style={{color:C.muted,fontSize:13,marginBottom:12}}>{emailModal.emailData?.adminNote}</p>
          <p style={{color:C.text,fontSize:13}}>This will send a payment reminder to the primary customer contact for <strong style={{color:C.accent}}>{emailModal.invoiceNum}</strong>.</p>
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setEmailModal(null)}>Cancel</Btn>
            <Btn disabled={saving} onClick={sendInvoiceEmail}>{saving?"Sending…":"Send Reminder"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── PortalAccessTab ───────────────────────────────────────────────────────────
function PortalAccessTab({ customer, onRefresh }) {
  const cid = customer.id;
  const users = customer.portalUsers || [];
  const [modal, setModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [sendingWelcome, setSendingWelcome] = useState(null);
  const [credModal, setCredModal] = useState(null); // { email, tempPassword }
  const INP = {width:"100%",padding:"8px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"};
  const SEL = {...INP,cursor:"pointer"};

  function openNew() { setEditUser(null); setForm({role:"viewer",isActive:true}); setMsg(""); setModal(true); }
  function openEdit(u) { setEditUser(u); setForm({firstName:u.firstName||"",lastName:u.lastName||"",role:u.role,isActive:u.isActive}); setMsg(""); setModal(true); }

  async function save() {
    if (!form.firstName?.trim()) { setMsg("First name is required."); return; }
    if (!form.lastName?.trim())  { setMsg("Last name is required."); return; }
    if (!editUser && !form.email?.trim()) { setMsg("Email is required."); return; }
    setSaving(true); setMsg("");
    try {
      if (editUser) {
        await api("PATCH", `/customers/${cid}/portal-users/${editUser.id}`, form);
      } else {
        const data = await api("POST", `/customers/${cid}/portal-users`, form);
        if (data.tempPassword) setCredModal({ email: form.email, tempPassword: data.tempPassword });
      }
      setModal(false); setEditUser(null); setForm({}); onRefresh();
    } catch(e) { setMsg(e.message); } finally { setSaving(false); }
  }

  async function sendWelcome(u) {
    setSendingWelcome(u.id);
    try {
      const data = await api("POST", `/customers/${cid}/portal-users/${u.id}/send-welcome`, {});
      setCredModal({ email: u.email, tempPassword: data.tempPassword, emailSent: true });
    } catch(e) { alert(e.message); } finally { setSendingWelcome(null); }
  }

  const roleColor = r => r==="admin"?"yellow":r==="manager"?"blue":"grey";

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,gap:12}}>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:C.text}}>Portal Users ({users.length})</div>
          <div style={{fontSize:12,color:C.muted,marginTop:3}}>Each user can log in to the customer portal at <span style={{color:C.accent}}>customer.rikai.tech</span></div>
        </div>
        <Btn variant="sm" onClick={openNew} style={{flexShrink:0}}>+ Add User</Btn>
      </div>
      {users.length===0 ? <EmptyState message="No portal users yet. Add users to grant customer portal access."/> : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
          {users.map(u=>(
            <div key={u.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:600,color:C.text,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.firstName} {u.lastName}</div>
                  <div style={{color:C.muted,fontSize:12,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end",flexShrink:0}}>
                  <Badge label={u.role} color={roleColor(u.role)}/>
                  <Badge label={u.isActive?"Active":"Inactive"} color={u.isActive?"green":"grey"}/>
                </div>
              </div>
              <div style={{fontSize:12,color:C.muted,marginBottom:12,paddingTop:8,borderTop:`1px solid ${C.border}`}}>
                Last login: <span style={{color:C.text}}>{u.lastLoginAt?fmt(u.lastLoginAt):"Never"}</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn variant="smGhost" onClick={()=>openEdit(u)}>Edit</Btn>
                <Btn variant="smInfo" disabled={sendingWelcome===u.id} onClick={()=>sendWelcome(u)}>
                  {sendingWelcome===u.id?"Sending…":"Send Welcome"}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{marginTop:32,padding:14,background:C.surface,borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,color:C.muted}}>
        <strong style={{color:C.text}}>Note:</strong> When you add a new user, a temporary password is auto-generated.
        Click <strong style={{color:C.accent}}>"Send Welcome"</strong> to email the portal URL and credentials to the user.
      </div>
      {modal&&(
        <Modal title={editUser?"Edit Portal User":"Add Portal User"} onClose={()=>{setModal(false);setForm({});}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="FIRST NAME *"><input style={INP} value={form.firstName||""} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))}/></Field>
            <Field label="LAST NAME *"><input style={INP} value={form.lastName||""} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))}/></Field>
          </div>
          {!editUser&&<Field label="EMAIL *"><input style={INP} type="email" value={form.email||""} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></Field>}
          <Field label="PHONE (optional)"><input style={INP} type="tel" placeholder="+91 98765 43210" value={form.phone||""} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/></Field>
          <Field label="ROLE">
            <select style={SEL} value={form.role||"viewer"} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
              <option value="viewer">Viewer — can view reports only</option>
              <option value="manager">Manager — can view + manage surveys</option>
              <option value="admin">Admin — full portal access</option>
            </select>
          </Field>
          {editUser&&<Field label="STATUS">
            <select style={SEL} value={form.isActive?"active":"inactive"} onChange={e=>setForm(p=>({...p,isActive:e.target.value==="active"}))}>
              <option value="active">Active</option><option value="inactive">Inactive (blocks login)</option>
            </select>
          </Field>}
          {msg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{msg}</div>}
          {!editUser&&<div style={{fontSize:12,color:C.muted,padding:"8px 10px",background:C.bg,borderRadius:6,marginBottom:10}}>A temporary password will be auto-generated. Use "Send Welcome" to email it to the user.</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>{setModal(false);setForm({});}}>Cancel</Btn><Btn disabled={saving} onClick={save}>{saving?"Saving…":"Save"}</Btn></div>
        </Modal>
      )}
      {credModal&&(
        <Modal title="Portal User Credentials" onClose={()=>setCredModal(null)}>
          {credModal.emailSent
            ? <div style={{fontSize:13,color:C.success,marginBottom:12}}>✓ Welcome email sent (if email service is configured). The new credentials are also shown below.</div>
            : <div style={{fontSize:13,color:C.muted,marginBottom:12}}>User created. Copy these credentials and share them securely — the password cannot be retrieved again.</div>
          }
          <Field label="EMAIL">
            <div style={{padding:"8px 10px",background:C.bg,borderRadius:6,fontSize:13,color:C.text,fontFamily:"monospace"}}>{credModal.email}</div>
          </Field>
          <Field label="TEMPORARY PASSWORD">
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{flex:1,padding:"10px 12px",background:C.bg,borderRadius:6,fontSize:14,color:C.accent,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em"}}>{credModal.tempPassword}</div>
              <Btn variant="sm" onClick={()=>{navigator.clipboard.writeText(credModal.tempPassword);}}>Copy</Btn>
            </div>
          </Field>
          <div style={{fontSize:11,color:C.muted,marginTop:4}}>The user must change this password after first login.</div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
            <Btn onClick={()=>setCredModal(null)}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── SurveyDetailPanel ─────────────────────────────────────────────────────────
const MODALITY_LABEL = { video:"Video (camera + mic)", voice:"Voice (mic only)", chat:"Chat (text)" };
const IconUnpublish = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5"/><path d="M5 12l7-7 7 7"/><circle cx="12" cy="21" r="1" fill="currentColor"/>
  </svg>
);

function SurveyDetailPanel({ surveyId, onClose, onRefresh }) {
  const [survey,  setSurvey]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [unpublishState, setUnpublishState] = useState(null);
  const [unpublishErr, setUnpublishErr]     = useState("");
  const [publishing, setPublishing]         = useState(false);
  const [publishErr, setPublishErr]         = useState("");

  // Ranking state
  const [rankingStatus,  setRankingStatus]  = useState(null); // null | { status, computedAt, version, counts }
  const [rankingPolling, setRankingPolling] = useState(false);

  // Invite participants state — single flow for both ranking-based and legacy
  const [invitePreview, setInvitePreview] = useState(null);  // null | "loading" | matching-participants response
  const [inviting,      setInviting]      = useState(false);
  const [inviteResult,  setInviteResult]  = useState(null);  // shown inside InviteMatchingModal
  const [inviteErr,     setInviteErr]     = useState("");

  // Survey detail tabs
  const [detailTab,       setDetailTab]       = useState("details");
  const [progress,        setProgress]        = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressFilter,  setProgressFilter]  = useState("all");
  const [closingManual,   setClosingManual]   = useState(false);
  const [reviewingId,     setReviewingId]     = useState(null); // sessionId with pending review action
  const [expandedGroups,  setExpandedGroups]  = useState({});   // participant key → true when attempt rows are expanded

  // Approval trail state
  const [approvalEvents,      setApprovalEvents]      = useState([]);
  const [approvalLoading,     setApprovalLoading]     = useState(false);
  const [sendingForReview,    setSendingForReview]    = useState(false);
  const [reviewNote,          setReviewNote]          = useState("");
  const [reviewErr,           setReviewErr]           = useState("");

  // Aggregate status tab
  const [aggStatus,        setAggStatus]        = useState(null);  // { included, queued, pendingReview, excluded, staleQueued }
  const [aggStatusLoading, setAggStatusLoading] = useState(false);
  const [recovering,       setRecovering]       = useState(false);
  const [recoveryResult,   setRecoveryResult]   = useState(null);  // { attempted, succeeded, failed }

  // Report state
  const [reportVersions,    setReportVersions]    = useState([]);
  const [reportLoading,     setReportLoading]     = useState(false);
  const [reportGenerating,  setReportGenerating]  = useState(false);
  const [reportError,       setReportError]       = useState("");
  const [reportWarning,     setReportWarning]     = useState("");
  const [selectedVersion,   setSelectedVersion]   = useState(null); // version number
  const [showInstructions,  setShowInstructions]  = useState(false);
  const pollRef = useRef(null); // interval id for report generation polling
  const [instructions,      setInstructions]      = useState("");
  const [publishingReport,  setPublishingReport]  = useState(false);
  const [downloadingPdf,    setDownloadingPdf]    = useState(false);

  const loadRankingStatus = async () => {
    try {
      const data = await api("GET", `/surveys/${surveyId}/ranking/status`);
      setRankingStatus(data);
      return data;
    } catch {
      // If the endpoint fails (e.g. DB columns not yet created), show a "none" state
      // so the admin can still see the Compute Ranking option
      setRankingStatus({ status: "none", computedAt: null, version: 0, counts: null });
      return null;
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api("GET", `/surveys/${surveyId}`).then(d => setSurvey(d.survey)).catch(e => setUnpublishErr(e.message)),
      loadRankingStatus(),
    ]).finally(() => setLoading(false));
  }, [surveyId]);

  const loadApprovalTrail = async () => {
    setApprovalLoading(true);
    try {
      const d = await api("GET", `/surveys/${surveyId}/approval-trail`);
      setApprovalEvents(d.events || []);
    } catch { /* ignore */ }
    finally { setApprovalLoading(false); }
  };

  useEffect(() => {
    if (detailTab === "trail") loadApprovalTrail();
  }, [detailTab, surveyId]);

  const handleSendForReview = async () => {
    setSendingForReview(true); setReviewErr("");
    try {
      await api("POST", `/surveys/${surveyId}/send-for-review`, { note: reviewNote });
      setSurvey(s => ({ ...s, status: "pending_review" }));
      setReviewNote("");
      if (detailTab === "trail") loadApprovalTrail();
    } catch(e) { setReviewErr(e.message || "Failed."); }
    finally { setSendingForReview(false); }
  };

  const handlePublish = async () => {
    setPublishing(true); setPublishErr("");
    try {
      await api("POST", `/surveys/${surveyId}/publish`);
      setSurvey(s => ({ ...s, status: "active", publishedAt: new Date().toISOString() }));
      onRefresh?.();
    } catch(e) { setPublishErr(e.message || "Failed to publish."); }
    finally { setPublishing(false); }
  };

  // Poll ranking status while computing
  useEffect(() => {
    if (!rankingPolling) return;
    const interval = setInterval(async () => {
      const data = await loadRankingStatus();
      if (data && data.status !== "computing") {
        setRankingPolling(false);
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [rankingPolling]);

  const loadProgress = async (flag) => {
    setProgressLoading(true);
    try {
      const qs = flag && flag !== "all" ? `?flag=${flag}` : "";
      const data = await api("GET", `/surveys/${surveyId}/sessions${qs}`);
      setProgress(data);
    } catch { /* non-fatal */ }
    finally { setProgressLoading(false); }
  };

  const loadAggStatus = async () => {
    setAggStatusLoading(true);
    setRecoveryResult(null);
    try {
      const data = await api("GET", `/surveys/${surveyId}/aggregate-status`);
      setAggStatus(data);
    } catch { /* non-fatal */ }
    finally { setAggStatusLoading(false); }
  };

  const handleRecoverAggregations = async () => {
    setRecovering(true);
    try {
      const result = await api("POST", `/surveys/${surveyId}/recover-aggregations`);
      setRecoveryResult(result);
      await loadAggStatus(); // refresh counts after recovery
    } catch (e) { alert(e.message || "Recovery failed."); }
    finally { setRecovering(false); }
  };

  useEffect(() => {
    if (detailTab === "progress")  loadProgress(progressFilter);
    if (detailTab === "report")    loadReport();
    if (detailTab === "aggregate") loadAggStatus();
  }, [detailTab, surveyId]); // eslint-disable-line

  const handleFilterChange = (f) => {
    setProgressFilter(f);
    loadProgress(f);
  };

  const handleManualClose = async () => {
    if (!window.confirm("Close this survey now? Remaining pending invitations will be expired and no new responses will be accepted.")) return;
    setClosingManual(true);
    try {
      await api("POST", `/surveys/${surveyId}/close`);
      onRefresh();
      // Reload survey in panel
      const d = await api("GET", `/surveys/${surveyId}`);
      setSurvey(d.survey);
    } catch(e) { alert(e.message || "Failed to close survey."); }
    finally { setClosingManual(false); }
  };

  const handleReview = async (sessionId, action) => {
    setReviewingId(sessionId);
    try {
      await api("PATCH", `/sessions/${sessionId}/review`, { action });
      await loadProgress(progressFilter);
    } catch(e) { alert(e.message || "Failed to update review."); }
    finally { setReviewingId(null); }
  };

  // ── Report helpers ─────────────────────────────────────────────────────
  // Returns the loaded versions array (or [] on "not yet generated") so callers can poll.
  const loadReport = async () => {
    setReportLoading(true); setReportError("");
    try {
      const data = await api("GET", `/surveys/${surveyId}/aggregate-report`);
      const versions = data.reports || [];
      setReportVersions(versions);
      if (versions.length && !selectedVersion) setSelectedVersion(versions[0].version);
      // If the newest version is still generating, reflect that in the UI.
      if (versions[0]?.status === "generating") setReportGenerating(true);
      return versions;
    } catch (e) {
      if (e.message?.includes("not yet generated")) { setReportVersions([]); return []; }
      setReportError(e.message);
      return [];
    } finally { setReportLoading(false); }
  };

  // Cleanup any in-flight poll on unmount / survey change.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, [surveyId]);

  // Poll GET …/aggregate-report until the given version leaves "generating".
  const pollForReport = (version) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const startedAt = Date.now();
    pollRef.current = setInterval(async () => {
      // Give up after 5 minutes — the worker should finish well within this.
      if (Date.now() - startedAt > 5 * 60 * 1000) {
        clearInterval(pollRef.current); pollRef.current = null;
        setReportGenerating(false);
        setReportError("Report is taking longer than expected. Refresh the tab in a minute to check again.");
        return;
      }
      let versions;
      try { versions = await api("GET", `/surveys/${surveyId}/aggregate-report`); }
      catch { return; } // transient — keep polling
      const list = versions.reports || [];
      setReportVersions(list);
      const row = list.find(r => r.version === version);
      if (!row || row.status === "generating") return; // still working
      clearInterval(pollRef.current); pollRef.current = null;
      setReportGenerating(false);
      if (row.status === "failed") {
        setReportError(row.generationError || "Report generation failed. Please retry.");
      } else {
        setSelectedVersion(version);
        if (row.content?._warning) setReportWarning(row.content._warning);
      }
    }, 5000);
  };

  const handleGenerateReport = async () => {
    // Warn if survey is still active
    if (survey?.status === "active") {
      const completedCount = survey._count?.sessions || survey.sessions?.length || "?";
      const target = survey.targetSampleSize || "?";
      if (!window.confirm(`This survey is still active (${completedCount}/${target} completions). The report will be based on incomplete data.\n\nGenerate anyway?`)) return;
    }
    setShowInstructions(true);
  };

  const confirmGenerate = async () => {
    setReportGenerating(true); setReportError(""); setReportWarning("");
    try {
      // Async: server returns 202 + { status:"generating", version }. We then poll.
      const data = await api("POST", `/surveys/${surveyId}/aggregate-report`, {
        instructions: instructions.trim() || null,
      });
      setShowInstructions(false);
      await loadReport();        // show the new "generating" placeholder row immediately
      if (data.version) {
        setSelectedVersion(data.version);
        pollForReport(data.version);
      } else {
        setReportGenerating(false);
      }
    } catch (e) { setReportError(e.message); setReportGenerating(false); }
  };

  const handlePublishReport = async (version) => {
    setPublishingReport(true); setReportError("");
    try {
      await api("PATCH", `/surveys/${surveyId}/aggregate-report/${version}/publish`);
      await loadReport();
    } catch (e) { setReportError(e.message); }
    finally { setPublishingReport(false); }
  };

  const handleUnpublishReport = async (version) => {
    if (!window.confirm("Unpublish this report? It will be removed from the customer's view.")) return;
    setPublishingReport(true); setReportError("");
    try {
      await api("PATCH", `/surveys/${surveyId}/aggregate-report/${version}/unpublish`);
      await loadReport();
    } catch (e) { setReportError(e.message); }
    finally { setPublishingReport(false); }
  };

  const handleUnpublishClick = async () => {
    setUnpublishErr(""); setUnpublishState("checking");
    try {
      await api("POST", `/surveys/${surveyId}/unpublish`);
      // No warning — went through cleanly
      onRefresh(); onClose();
    } catch(e) {
      if (e.message && e.message.startsWith("Survey has active data")) {
        // shouldn't reach here — server returns 409 JSON
      }
      // Try parsing 409 warning from the raw response
      setUnpublishState(null);
      setUnpublishErr(e.message||"Failed.");
    }
  };

  // Called from fetch so we can read the raw JSON on 409
  const initiateUnpublish = async () => {
    setUnpublishErr(""); setUnpublishState("checking");
    const res = await fetch(`/api/admin/surveys/${surveyId}/unpublish`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json();
    if (res.status === 409) {
      setUnpublishState({ pendingInvitations: data.pendingInvitations, sessionCount: data.sessionCount });
    } else if (!res.ok) {
      setUnpublishState(null); setUnpublishErr(data.error || "Failed to unpublish.");
    } else {
      onRefresh(); onClose();
    }
  };

  const forceUnpublish = async () => {
    setUnpublishState("unpublishing"); setUnpublishErr("");
    const res = await fetch(`/api/admin/surveys/${surveyId}/unpublish?force=true`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json();
    if (!res.ok) { setUnpublishState(null); setUnpublishErr(data.error || "Failed."); }
    else { onRefresh(); onClose(); }
  };

  const computeRanking = async () => {
    setInviteErr("");
    try {
      await api("POST", `/surveys/${surveyId}/compute-ranking`);
      setRankingStatus(s => ({ ...s, status: "computing" }));
      setRankingPolling(true);
    } catch(e) { setInviteErr(e.message||"Failed to start ranking."); }
  };

  // Invite flow: ranking must be ready before invitations can be sent.
  // The analysis modal (matching-participants preview) is available at any time for inspection.
  const initiateInvite = async () => {
    setInviteErr(""); setInvitePreview("loading"); setInviteResult(null);
    try {
      const data = await api("GET", `/surveys/${surveyId}/matching-participants`);
      setInvitePreview(data);
    } catch(e) { setInvitePreview(null); setInviteErr(e.message||"Failed to load matching analysis."); }
  };

  const confirmInvite = async () => {
    setInviting(true); setInviteErr("");
    try {
      const result = await api("POST", `/surveys/${surveyId}/invite-from-ranking`, {});
      setInviteResult(result); // shown inline inside InviteMatchingModal — modal stays open
      onRefresh();
      await loadRankingStatus();
    } catch(e) { setInviteErr(e.message||"Failed to send invitations."); }
    finally { setInviting(false); }
  };

  const closeInviteModal = () => {
    setInvitePreview(null); setInviteResult(null); setInviteErr("");
  };

  const statusColor = s => ({active:"#22C55E",closed:"#EF4444",approved:"#22C55E",pending_review:"#F59E0B"}[s] || "#64748B");
  const DRow = ({label, val}) => val ? (
    <div style={{display:"flex",gap:12,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
      <span style={{width:140,flexShrink:0,fontSize:12,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{label}</span>
      <span style={{fontSize:13,color:C.text}}>{val}</span>
    </div>
  ) : null;

  const DText = ({label, text}) => text ? (
    <div style={{marginTop:12}}>
      <div style={{fontSize:12,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{label}</div>
      <div style={{fontSize:13,color:C.text,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{text}</div>
    </div>
  ) : null;

  return (
    <div style={{background:C.bg,minHeight:"100dvh",fontFamily:"'Poppins',sans-serif",display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:`1px solid ${C.border}`,background:C.surface,flexShrink:0}}>
        <button onClick={onClose} style={{background:"transparent",border:"none",color:C.muted,cursor:"pointer",fontSize:22,lineHeight:1,display:"flex",alignItems:"center",padding:4,minWidth:44,minHeight:44}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:15,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{survey?.title||"Survey Detail"}</div>
          {survey && <div style={{fontSize:11,color:C.muted,marginTop:1}}>Read-only view · {survey.status?.replace(/_/g," ")}</div>}
        </div>
        {/* Send for Review button — visible for draft surveys only; once approved the next action is Publish */}
        {survey && survey.status === "draft" && (
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
            <button onClick={handleSendForReview} disabled={sendingForReview}
              style={{padding:"8px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid #8B5CF6`,background:"#8B5CF618",color:"#8B5CF6",fontFamily:"inherit",opacity:sendingForReview?0.5:1,whiteSpace:"nowrap"}}>
              {sendingForReview ? "Sending…" : "→ Send for Review"}
            </button>
            {reviewErr && <div style={{fontSize:11,color:C.danger}}>{reviewErr}</div>}
          </div>
        )}
        {/* Publish button — disabled (not hidden) until customer approves */}
        {survey && !["active","closed"].includes(survey.status) && (
          <div style={{position:"relative",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
            <button
              onClick={handlePublish}
              disabled={survey.status !== "approved" || publishing}
              title={survey.status !== "approved" ? "Customer must approve the survey before it can be published" : "Publish survey"}
              style={{padding:"8px 14px",borderRadius:7,fontSize:12,fontWeight:600,border:`1.5px solid ${survey.status==="approved"?"#22C55E":C.border}`,background:survey.status==="approved"?"#22C55E18":"transparent",color:survey.status==="approved"?"#22C55E":C.muted,fontFamily:"inherit",cursor:(survey.status==="approved"&&!publishing)?"pointer":"not-allowed",whiteSpace:"nowrap",opacity:survey.status==="approved"?(publishing?0.5:1):0.5}}>
              {publishing ? "Publishing…" : "Publish"}
            </button>
            {publishErr && <div style={{fontSize:11,color:C.danger}}>{publishErr}</div>}
          </div>
        )}
        {survey?.status==="active" && (<>
          {/* Ranking status + compute button */}
          {rankingStatus && (() => {
            const rs = rankingStatus.status;
            const isComputing = rs === "computing";
            const isReady     = rs === "ready";
            const color = isReady ? "#22C55E" : isComputing ? "#F59E0B" : "#64748B";
            const label = isReady
              ? `Ranked · ${rankingStatus.counts?.total ?? "?"} candidates`
              : isComputing ? "Computing ranking…"
              : rs === "stale" ? "Ranking stale" : "No ranking";
            return (
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,color,fontWeight:600,whiteSpace:"nowrap"}}>
                  {isComputing ? "⟳ " : isReady ? "✓ " : "○ "}{label}
                </span>
                {!isComputing && !isReady && (
                  <button onClick={computeRanking}
                    style={{padding:"6px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid #F59E0B`,background:"#F59E0B18",color:"#F59E0B",fontFamily:"inherit"}}>
                    Compute Ranking
                  </button>
                )}
                {rs === "stale" && (
                  <button onClick={computeRanking}
                    style={{padding:"6px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid #F59E0B`,background:"#F59E0B18",color:"#F59E0B",fontFamily:"inherit"}}>
                    Re-rank
                  </button>
                )}
              </div>
            );
          })()}
          <button onClick={initiateInvite} disabled={invitePreview==="loading"||inviting}
            style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${C.accent}`,background:rankingStatus?.status==="ready"?C.accentDim:"transparent",color:C.accent,fontFamily:"inherit",opacity:(invitePreview==="loading"||inviting)?0.5:1}}>
            {invitePreview==="loading" ? "Analysing…" : rankingStatus?.status==="ready" ? "✉ Invite from Ranking" : "✉ Invite Participants"}
          </button>
          <button onClick={initiateUnpublish} disabled={unpublishState==="checking"||unpublishState==="unpublishing"}
            style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${C.danger}`,background:"transparent",color:C.danger,fontFamily:"inherit",opacity:(unpublishState==="checking"||unpublishState==="unpublishing")?0.5:1}}>
            <IconUnpublish/>{unpublishState==="checking"?"Checking…":"Unpublish"}
          </button>
        </>)}
      </div>

      {/* Unpublish warning modal */}
      {unpublishState && unpublishState!=="checking" && unpublishState!=="unpublishing" && (
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:C.card,border:`1px solid ${C.danger}`,borderRadius:12,padding:28,maxWidth:440,width:"90%"}}>
            <div style={{fontWeight:700,fontSize:15,color:C.text,marginBottom:10}}>⚠️ Active Data Warning</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:8,lineHeight:1.6}}>
              This survey has active data that will be affected:
            </div>
            <ul style={{margin:"0 0 16px 0",paddingLeft:20,fontSize:13,color:C.text,lineHeight:2}}>
              {unpublishState.pendingInvitations>0 && <li><strong>{unpublishState.pendingInvitations}</strong> pending invitation{unpublishState.pendingInvitations!==1?"s":""} will be <span style={{color:C.danger}}>deleted</span></li>}
              {unpublishState.sessionCount>0 && <li><strong>{unpublishState.sessionCount}</strong> session record{unpublishState.sessionCount!==1?"s":""} will be <span style={{color:"#F59E0B"}}>retained</span> (not deleted)</li>}
            </ul>
            <div style={{fontSize:12,color:C.muted,marginBottom:20}}>The survey will revert to draft status. Completed sessions and their data are preserved.</div>
            {unpublishErr && <div style={{color:C.danger,fontSize:13,marginBottom:10}}>{unpublishErr}</div>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>{setUnpublishState(null);setUnpublishErr("");}}>Cancel</Btn>
              <Btn variant="danger" onClick={forceUnpublish}>Proceed — Unpublish</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Unified invite modal — used for both ranking-based and legacy paths */}
      {(invitePreview || inviting || inviteResult) && (
        <InviteMatchingModal
          preview={invitePreview}
          inviting={inviting}
          result={inviteResult}
          error={inviteErr}
          rankingReady={rankingStatus?.status === "ready"}
          subtitle={rankingStatus?.status === "ready"
            ? `Sending from persisted ranking · v${rankingStatus.version} · ${rankingStatus.counts?.total ?? "?"} candidates`
            : "Ranking not computed — analysis only. Compute a ranking to enable sending."}
          onCancel={closeInviteModal}
          onConfirm={confirmInvite}
        />
      )}

      {/* Detail tab bar */}
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,background:C.surface,flexShrink:0,overflowX:"auto",scrollbarWidth:"none"}}>
        {[{key:"details",label:"Details"},{key:"progress",label:"Progress"},{key:"report",label:"Report"},{key:"aggregate",label:"Aggregate"},{key:"trail",label:"Approval Trail"}].map(t=>(
          <button key={t.key} onClick={()=>setDetailTab(t.key)} style={{
            background:"transparent",border:"none",cursor:"pointer",padding:"11px 18px",
            fontSize:13,fontWeight:600,fontFamily:"inherit",whiteSpace:"nowrap",
            color:detailTab===t.key?C.accent:C.muted,
            borderBottom:detailTab===t.key?`2px solid ${C.accent}`:"2px solid transparent",
            minHeight:44,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      <div style={{flex:1,overflowY:"auto",padding:"24px 28px",maxWidth:detailTab==="progress"||detailTab==="report"?1100:760,width:"100%",margin:"0 auto",boxSizing:"border-box"}}>
        {loading ? <div style={{color:C.muted,fontSize:14}}>Loading…</div> : !survey ? (
          <div style={{color:C.danger}}>Failed to load survey.</div>
        ) : (<>
          {detailTab === "details" && <>
          {inviteErr && !invitePreview && !(inviteResult) && (
            <div style={{marginBottom:12,padding:"8px 14px",borderRadius:8,background:"#EF444422",border:`1px solid ${C.danger}`,fontSize:13,color:C.danger}}>{inviteErr}</div>
          )}

          {/* Status banner */}
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24,padding:"10px 16px",borderRadius:8,background:`${statusColor(survey.status)}18`,border:`1px solid ${statusColor(survey.status)}44`}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:statusColor(survey.status),display:"inline-block",flexShrink:0}}/>
            <span style={{fontSize:13,fontWeight:700,color:statusColor(survey.status),textTransform:"uppercase",letterSpacing:0.5}}>{survey.status}</span>
            {survey.publishedAt && <span style={{fontSize:12,color:C.muted}}>· Published {fmt(survey.publishedAt)}</span>}
          </div>

          {/* Ranking status panel */}
          {survey.status === "active" && rankingStatus && (
            <div style={{marginBottom:20,padding:"12px 16px",borderRadius:8,background:C.surface,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>
                    {rankingStatus.status === "ready"    ? "✓ Ranking Ready"
                   : rankingStatus.status === "computing" ? "⟳ Computing Ranking…"
                   : rankingStatus.status === "stale"    ? "△ Ranking Stale"
                   : "○ No Ranking Computed"}
                  </div>
                  <div style={{fontSize:11,color:C.muted}}>
                    {rankingStatus.status === "ready" && rankingStatus.counts
                      ? `${rankingStatus.counts.total} candidates · T1: ${rankingStatus.counts.tier1} · T2: ${rankingStatus.counts.tier2} · T3: ${rankingStatus.counts.tier3} · Computed ${new Date(rankingStatus.computedAt).toLocaleString()}`
                      : rankingStatus.status === "computing"
                      ? "Semantic scoring in progress — this may take 1–2 minutes for large pools"
                      : rankingStatus.status === "stale"
                      ? "Survey filters changed since last ranking. Re-rank to update."
                      : "Compute a ranking to enable rank-ordered invitation batches with full fatigue protection."}
                  </div>
                </div>
                {rankingStatus.status !== "computing" && (
                  <button onClick={computeRanking}
                    style={{padding:"7px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid #F59E0B`,background:"#F59E0B18",color:"#F59E0B",fontFamily:"inherit",flexShrink:0}}>
                    {rankingStatus.status === "ready" ? "Re-rank" : rankingStatus.status === "stale" ? "Re-rank Now" : "Compute Ranking"}
                  </button>
                )}
              </div>
              {inviteErr && <div style={{fontSize:12,color:C.danger,marginTop:8}}>{inviteErr}</div>}
            </div>
          )}

          {/* Invitation stats (if any) */}
          {survey.invitations?.length > 0 && (() => {
            const pending   = survey.invitations.filter(i=>i.status==="pending").length;
            const completed = survey.invitations.filter(i=>i.status==="completed").length;
            const total     = survey.invitations.length;
            return (
              <Section title="Invitations">
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:4}}>
                  {[
                    {label:"Total Sent",  value:total,     color:C.accent},
                    {label:"Pending",     value:pending,   color:"#F59E0B"},
                    {label:"Completed",   value:completed, color:"#22C55E"},
                  ].map(s=>(
                    <div key={s.label} style={{background:C.surface,borderRadius:8,padding:"10px 14px",border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{s.label}</div>
                      <div style={{fontSize:22,fontWeight:700,color:s.color}}>{s.value}</div>
                      {s.label==="Completed"&&total>0&&<div style={{fontSize:10,color:C.muted,marginTop:2}}>{Math.round(completed/total*100)}% completion</div>}
                    </div>
                  ))}
                </div>
              </Section>
            );
          })()}

          {/* Basic info */}
          <Section title="Basic Info">
            <DRow label="Title"       val={survey.title}/>
            <DRow label="Category"    val={survey.category}/>
            <DRow label="Type"        val={survey.surveyType?.replace(/_/g," ")}/>
            <DRow label="Agent"       val={survey.agent}/>
            <DRow label="Modality"    val={MODALITY_LABEL[survey.modality]||survey.modality}/>
            <DRow label="Multilingual" val={survey.multilingualEnabled ? "Enabled" : "Disabled"}/>
            <DRow label="Language"    val={survey.language}/>
            <DRow label="Duration"    val={survey.estimatedMins ? `${survey.estimatedMins} min` : null}/>
            <DRow label="Sample size" val={survey.targetSampleSize}/>
            <DRow label="Incentive"   val={survey.incentiveType&&survey.incentiveType!=="none" ? `${survey.incentiveType}${survey.incentiveValue?` — ₹${survey.incentiveValue}`:""}` : null}/>
            <DRow label="Close date"  val={survey.closeDate ? fmt(survey.closeDate) : null}/>
            <DText label="Description"  text={survey.description}/>
            <DText label="Purpose"      text={survey.purpose}/>
            <DText label="Instructions" text={survey.instructions}/>
          </Section>

          {/* Questions */}
          <Section title={`Questions (${(survey.questions||[]).length})`}>
            {(survey.questions||[]).length===0 ? <div style={{fontSize:13,color:C.muted}}>No questions.</div> :
              (survey.questions||[]).map((q,i)=>(
                <div key={q.id||i} style={{padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                    <span style={{fontSize:11,fontWeight:700,color:C.accent,background:C.accentDim,borderRadius:4,padding:"2px 6px",flexShrink:0}}>Q{i+1}</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,color:C.text,lineHeight:1.5}}>{q.questionText}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:3}}>{q.questionType}{q.isScreener?" · screener":""}</div>
                    </div>
                  </div>
                </div>
              ))
            }
          </Section>

          {/* Targeting */}
          <Section title="Targeting">
            {[
              ["Age Bands",    survey.targetAgeBands],
              ["Genders",      survey.targetGenders],
              ["City Tiers",   survey.targetCityTiers],
              ["Languages",    survey.targetLanguages],
              ["Education",    survey.targetEducation],
              ["Income Bands", survey.targetIncomeBands],
              ["Professions",  survey.targetProfessions],
              ["Household",    survey.targetHouseholdRoles],
              ["Experience",   survey.targetYearsWorking],
              ["Industries",   survey.targetIndustries],
            ].map(([label, val])=> {
              const arr = Array.isArray(val) ? val : (val ? [val] : []);
              if (!arr.length) return null;
              return (
                <div key={label} style={{marginBottom:10}}>
                  <div style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:5}}>{label}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                    {arr.map(v=><span key={v} style={{fontSize:12,padding:"3px 9px",borderRadius:99,background:C.accentDim,color:C.accent,fontWeight:500}}>{v}</span>)}
                  </div>
                </div>
              );
            })}
          </Section>

          {unpublishErr && <div style={{color:C.danger,fontSize:13,marginTop:12}}>{unpublishErr}</div>}
          </>}

          {/* ── PROGRESS TAB ────────────────────────────────────────────── */}
          {/* ── REPORT TAB ─────────────────────────────────────────────── */}
          {detailTab === "report" && (() => {
            const currentReport = reportVersions.find(r => r.version === selectedVersion);
            let content = currentReport?.content;

            // ── Client-side JSON repair + truncation recovery ──────────────────────
            const repairJsonClient = (s) => {
              let inStr = false, esc = false, out = "";
              for (let i = 0; i < s.length; i++) {
                const ch = s[i];
                if (esc)         { out += ch; esc = false; continue; }
                if (ch === "\\") { out += ch; esc = true;  continue; }
                if (ch === '"')  { out += ch; inStr = !inStr; continue; }
                if (inStr) {
                  if (ch === "\n") { out += "\\n"; continue; }
                  if (ch === "\r") { out += "\\r"; continue; }
                  if (ch === "\t") { out += "\\t"; continue; }
                }
                out += ch;
              }
              return out;
            };
            const jsonStateClient2 = (str) => {
              let stack = [], inStr2 = false, esc2 = false;
              for (let i = 0; i < str.length; i++) {
                const ch = str[i];
                if (esc2)        { esc2 = false; continue; }
                if (ch === "\\") { esc2 = true;  continue; }
                if (ch === '"')  { inStr2 = !inStr2; continue; }
                if (!inStr2) {
                  if      (ch === "{" || ch === "[") stack.push(ch === "{" ? "}" : "]");
                  else if (ch === "}" || ch === "]") stack.pop();
                }
              }
              return { stack: stack.reverse(), inStr: inStr2 };
            };
            const truncRecoverClient = (raw) => {
              const tryClose2 = (str) => {
                const { stack, inStr } = jsonStateClient2(str);
                if (!stack.length && !inStr) return null;
                const suffix = (inStr ? '"' : "") + stack.join("");
                try { const r = JSON.parse(str + suffix); r._truncated = true; return r; } catch { return null; }
              };
              let res = tryClose2(raw); if (res) return res;
              const commas = [];
              let inS = false, escc = false, dep = 0;
              for (let i = 0; i < raw.length; i++) {
                const ch = raw[i];
                if (escc)        { escc = false; continue; }
                if (ch === "\\") { escc = true;  continue; }
                if (ch === '"')  { inS = !inS;   continue; }
                if (!inS) {
                  if (ch === "{" || ch === "[") dep++;
                  else if (ch === "}" || ch === "]") dep--;
                  else if (ch === "," && dep > 0) commas.push(i);
                }
              }
              for (let k = commas.length - 1; k >= Math.max(0, commas.length - 40); k--) {
                res = tryClose2(raw.slice(0, commas[k])); if (res) return res;
              }
              return null;
            };
            const parseAiJsonClient = (text) => {
              let s = text.replace(/^```(?:json)?\s*/i,"").replace(/\s*```\s*$/,"").trim();
              const complete = s.match(/\{[\s\S]*\}/);
              if (complete) { s = complete[0]; } else { const p = s.match(/\{[\s\S]*/); if (p) s = p[0]; }
              s = repairJsonClient(s);
              try { return JSON.parse(s); } catch {
                const rec = truncRecoverClient(s); if (rec) return rec;
                throw new Error("JSON parse failed");
              }
            };

            if (typeof content === "string") { try { content = parseAiJsonClient(content); } catch {} }
            if (content?.raw && typeof content.raw === "string" && !content.executiveSummary && !content.keyThemes && !content.questionAnalysis) {
              try { content = { ...parseAiJsonClient(content.raw), _stats: content._stats }; } catch {}
            }
            const isTruncated = !!content?._truncated;
            const isRaw       = !!(content?.raw && !content.executiveSummary && !content.keyThemes && !content.questionAnalysis);
            const statusColor = { draft: C.muted, published: "#22C55E", revision_requested: "#F59E0B" };
            const statusLabel = { generating: "GENERATING…", draft: "DRAFT", published: "PUBLISHED", revision_requested: "REVISION REQUESTED", failed: "FAILED" };

            return (
              <div>
                {/* Instructions modal */}
                {showInstructions && (
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
                    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,padding:24,maxWidth:560,width:"100%",maxHeight:"80vh",overflow:"auto"}}>
                      <h3 style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:4}}>Generate Report</h3>
                      <p style={{fontSize:12,color:C.muted,marginBottom:16}}>
                        Optionally provide additional instructions for the AI analyst — e.g. "Focus on the 25-34 age segment", "Exclude Q3", "Add competitive benchmarking section".
                      </p>
                      <textarea
                        value={instructions} onChange={e=>setInstructions(e.target.value)}
                        rows={5} placeholder="Additional instructions (optional)…"
                        style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:13,padding:"10px 12px",outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}
                      />
                      {reportError && <div style={{color:C.danger,fontSize:12,marginTop:8}}>{reportError}</div>}
                      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
                        <button onClick={()=>{ setShowInstructions(false); setReportError(""); }}
                          style={{padding:"8px 16px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontFamily:"inherit"}}>
                          Cancel
                        </button>
                        <button onClick={confirmGenerate} disabled={reportGenerating}
                          style={{padding:"8px 16px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:C.accent,color:"#fff",fontFamily:"inherit",opacity:reportGenerating?0.5:1}}>
                          {reportGenerating ? "Generating…" : "Generate"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header: Generate button + version picker */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:20,flexWrap:"wrap"}}>
                  <div>
                    <h3 style={{fontSize:18,fontWeight:700,color:C.text,marginBottom:2}}>Aggregate Report</h3>
                    <p style={{fontSize:12,color:C.muted}}>
                      {reportVersions.length ? `${reportVersions.length} version${reportVersions.length>1?"s":""} generated` : "No report generated yet"}
                    </p>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    {reportVersions.length > 0 && (
                      <select value={selectedVersion||""} onChange={e=>{setSelectedVersion(Number(e.target.value)); setReportError("");}}
                        style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,color:C.text,fontSize:12,padding:"7px 10px",fontFamily:"inherit",outline:"none"}}>
                        {reportVersions.map(r=>(
                          <option key={r.version} value={r.version}>v{r.version} — {statusLabel[r.status]||r.status}</option>
                        ))}
                      </select>
                    )}
                    <button onClick={handleGenerateReport} disabled={reportGenerating}
                      style={{padding:"8px 16px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:C.accent,color:"#fff",fontFamily:"inherit",opacity:reportGenerating?0.5:1}}>
                      {reportVersions.length ? "Regenerate with Edits" : "Generate Report"}
                    </button>
                  </div>
                </div>

                {reportWarning && (
                  <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:"#F59E0B12",border:"1px solid #F59E0B33",fontSize:12,color:"#F59E0B"}}>
                    ⚠ {reportWarning}
                  </div>
                )}
                {isTruncated && (
                  <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:"#F59E0B12",border:"1px solid #F59E0B33",fontSize:12,color:"#F59E0B"}}>
                    ⚠ This report was truncated due to response size — some tail sections (methodology / conclusion) may be abbreviated. Regenerate to get the full report.
                  </div>
                )}
                {isRaw && (
                  <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:"#EF444412",border:"1px solid #EF444433",fontSize:12,color:C.danger}}>
                    ✗ Report content could not be parsed. Please regenerate the report.
                  </div>
                )}
                {reportError && !showInstructions && (
                  <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:"#EF444422",border:`1px solid ${C.danger}`,fontSize:12,color:C.danger}}>{reportError}</div>
                )}

                {reportLoading && <div style={{color:C.muted,fontSize:13,padding:40,textAlign:"center"}}>Loading report…</div>}

                {/* No report state */}
                {!reportLoading && reportVersions.length === 0 && (
                  <div style={{textAlign:"center",padding:60,background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:36,marginBottom:12}}>📊</div>
                    <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>No report generated yet</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Generate a cross-participant AI analysis from completed session data.</div>
                    <button onClick={handleGenerateReport}
                      style={{padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",border:"none",background:C.accent,color:"#fff",fontFamily:"inherit"}}>
                      Generate Report
                    </button>
                  </div>
                )}

                {/* Generating state — heavy Claude call runs in a Cloud Tasks worker */}
                {!reportLoading && currentReport?.status === "generating" && (
                  <div style={{textAlign:"center",padding:60,background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:36,marginBottom:12}}>⏳</div>
                    <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>Generating report…</div>
                    <div style={{fontSize:12,color:C.muted}}>This usually takes 1–2 minutes. You can leave this tab open — it will update automatically.</div>
                  </div>
                )}

                {/* Failed state — offer retry */}
                {!reportLoading && currentReport?.status === "failed" && (
                  <div style={{textAlign:"center",padding:60,background:C.surface,borderRadius:10,border:`1px solid ${C.danger}`}}>
                    <div style={{fontSize:36,marginBottom:12}}>✗</div>
                    <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>Report generation failed</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:20}}>{currentReport.generationError || "Something went wrong. Please try again."}</div>
                    <button onClick={handleGenerateReport} disabled={reportGenerating}
                      style={{padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",border:"none",background:C.accent,color:"#fff",fontFamily:"inherit",opacity:reportGenerating?0.5:1}}>
                      Retry
                    </button>
                  </div>
                )}

                {/* Report viewer */}
                {!reportLoading && currentReport && currentReport.status !== "generating" && currentReport.status !== "failed" && content && (
                  <div>
                    {/* Version metadata + publish controls */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"14px 18px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`,marginBottom:16,flexWrap:"wrap"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                        <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                          background:`${statusColor[currentReport.status]||C.muted}22`,
                          color:statusColor[currentReport.status]||C.muted,
                          textTransform:"uppercase",letterSpacing:0.5}}>
                          {statusLabel[currentReport.status]||currentReport.status}
                        </span>
                        <span style={{fontSize:12,color:C.muted}}>
                          v{currentReport.version} · Generated {new Date(currentReport.generatedAt).toLocaleString("en-IN")}
                        </span>
                        {currentReport.generatedBy && <span style={{fontSize:11,color:C.muted}}>by {currentReport.generatedBy}</span>}
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button
                          disabled={downloadingPdf}
                          onClick={async () => {
                            setDownloadingPdf(true);
                            try {
                              await downloadReportPdf({
                                content,
                                reportMeta: currentReport,
                                surveyTitle: survey?.title,
                                companyName: null,
                              });
                            } catch(e) { alert("PDF generation failed: " + e.message); }
                            finally { setDownloadingPdf(false); }
                          }}
                          style={{padding:"7px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${C.info}`,background:"#3B82F618",color:C.info,fontFamily:"inherit",opacity:downloadingPdf?0.5:1}}>
                          {downloadingPdf ? "Generating…" : "⬇ Download PDF"}
                        </button>
                        {currentReport.status !== "published" && (
                          <button onClick={()=>handlePublishReport(currentReport.version)} disabled={publishingReport}
                            style={{padding:"7px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid #22C55E",background:"#22C55E18",color:"#22C55E",fontFamily:"inherit",opacity:publishingReport?0.5:1}}>
                            {publishingReport ? "…" : "Publish to Customer"}
                          </button>
                        )}
                        {(currentReport.status === "published" || currentReport.status === "revision_requested") && (
                          <button onClick={()=>handleUnpublishReport(currentReport.version)} disabled={publishingReport}
                            style={{padding:"7px 14px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${C.danger}`,background:"#EF444418",color:C.danger,fontFamily:"inherit",opacity:publishingReport?0.5:1}}>
                            {publishingReport ? "…" : "Unpublish"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Instructions used */}
                    {currentReport.instructions && (
                      <div style={{marginBottom:16,padding:"12px 16px",borderRadius:8,background:"#3B82F612",border:"1px solid #3B82F633"}}>
                        <div style={{fontSize:11,fontWeight:700,color:C.info,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Instructions Used</div>
                        <div style={{fontSize:13,color:C.text,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{currentReport.instructions}</div>
                      </div>
                    )}

                    {/* Customer feedback */}
                    {currentReport.feedback?.length > 0 && (
                      <div style={{marginBottom:16,padding:"14px 16px",borderRadius:10,background:"#F59E0B0A",border:"1px solid #F59E0B33"}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#F59E0B",textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Customer Feedback</div>
                        {currentReport.feedback.map(fb=>(
                          <div key={fb.id} style={{marginBottom:8,padding:"10px 12px",background:C.card,borderRadius:8,border:`1px solid ${C.border}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                              <span style={{fontSize:12,fontWeight:600,color:C.text}}>{fb.customer?.companyName||"Customer"}</span>
                              <span style={{fontSize:10,color:C.muted}}>{new Date(fb.createdAt).toLocaleString("en-IN")}</span>
                            </div>
                            <div style={{fontSize:13,color:C.text,lineHeight:1.5}}>{fb.comment}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Report content */}
                    <div style={{display:"flex",flexDirection:"column",gap:16}}>

                      {/* Executive Summary */}
                      {content.executiveSummary && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Executive Summary</h4>
                          <div style={{fontSize:13,color:C.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{content.executiveSummary}</div>
                        </div>
                      )}

                      {/* Methodology Scorecard (Phase 3) — deterministic KPIs; omitted for qual_only */}
                      {content.scorecard && content.scorecard.headline ? (() => {
                        const sc = content.scorecard;
                        const hd = sc.headline;
                        const bandColor = (b) => {
                          const u = String(b||"").toUpperCase();
                          if (["GO","EXCELLENT"].includes(u)) return "#22C55E";
                          if (["REFINE","GOOD"].includes(u)) return "#F59E0B";
                          if (["KILL","NEEDS ATTENTION"].includes(u)) return "#EF4444";
                          return C.accent;
                        };
                        const fmt = (v,u) => {
                          if (v == null) return "—";
                          if (u === "nps" || !u) return String(v);
                          if (u === "%" || String(u).startsWith("% ")) return `${v}%`;
                          if (String(u).startsWith("/")) return `${v}${u}`;
                          if (u === "₹") return `₹${v}`;
                          return `${v} ${u}`;
                        };
                        // "By segment" cuts are disabled — segment n counts silently exclude
                        // supplier-sourced respondents (no demographic profile on file for
                        // them), so the base shown would be misleading. See docs/product-backlog.md.
                        const segments = [];
                        const maxPct = sc.funnel ? Math.max(1, ...sc.funnel.map(f=>f.pct||0)) : 1;
                        return (
                          <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                            <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Methodology Scorecard</h4>
                            {/* Headline */}
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,padding:"14px 16px",borderRadius:10,background:C.surface,marginBottom:12}}>
                              <div>
                                <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:0.6,marginBottom:4}}>{hd.label}</div>
                                <div style={{fontSize:34,fontWeight:800,color:C.accent,lineHeight:1}}>{hd.value==null?"—":fmt(hd.value,hd.unit)}</div>
                                {hd.interpretation && <div style={{fontSize:11,color:C.muted,marginTop:6}}>{hd.interpretation}</div>}
                                {hd.norm && <div style={{fontSize:11,color:C.muted,marginTop:4}}>
                                  vs benchmark <strong style={{color:C.text}}>{fmt(hd.norm.value,hd.unit)}</strong>
                                  {hd.norm.delta!=null && <span style={{marginLeft:6,fontWeight:700,color:hd.norm.delta>=0?"#15803D":"#B91C1C"}}>{hd.norm.delta>=0?"▲ +":"▼ "}{hd.norm.delta}</span>}
                                  <span style={{marginLeft:6,opacity:0.7}}>({hd.norm.sourceType})</span>
                                </div>}
                              </div>
                              {hd.band && <div style={{fontSize:13,fontWeight:800,color:"#fff",background:bandColor(hd.band),padding:"6px 14px",borderRadius:8,textTransform:"uppercase",letterSpacing:0.6}}>{hd.band}</div>}
                            </div>
                            {/* Metrics */}
                            {(sc.metrics||[]).length>0 && (
                              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))",gap:10,marginBottom:segments.length||sc.funnel?12:0}}>
                                {sc.metrics.slice(0,6).map((m,i)=>(
                                  <div key={i} style={{padding:"10px 12px",background:C.surface,borderRadius:8,textAlign:"center"}}>
                                    <div style={{fontSize:18,fontWeight:800,color:C.text}}>{fmt(m.value,m.unit)}</div>
                                    <div style={{fontSize:10,color:C.muted,marginTop:2,textTransform:"uppercase",letterSpacing:0.4}}>{m.label}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Funnel */}
                            {sc.funnel?.length>0 && (
                              <div style={{marginBottom:segments.length?12:0}}>
                                {sc.funnel.map((f,i)=>(
                                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                                    <div style={{fontSize:11,color:C.text,width:150}}>{f.label}</div>
                                    <div style={{flex:1,height:12,background:C.surface,borderRadius:6,overflow:"hidden"}}>
                                      <div style={{height:12,width:`${Math.round(((f.pct||0)/maxPct)*100)}%`,background:C.accent,borderRadius:6}}/>
                                    </div>
                                    <div style={{fontSize:11,fontWeight:700,color:C.accent,width:48,textAlign:"right"}}>{f.pct==null?"—":`${f.pct}%`}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* By segment */}
                            {segments.length>0 && (
                              <div>
                                <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,margin:"4px 0 6px"}}>{hd.label} by Segment</div>
                                {segments.map((s,i)=>(
                                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                                    <div style={{fontSize:12,color:C.text}}>{s.dimensionLabel}: {s.segment} <span style={{fontSize:11,color:C.muted}}>(n={s.base})</span></div>
                                    {s.suppressed ? <div style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>insufficient base</div> : <div style={{fontSize:12,fontWeight:700,color:C.accent}}>{fmt(s.value,s.unit)}</div>}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Detractor verbatims */}
                            {sc.drivers?.detraction?.length>0 && (
                              <div style={{marginTop:12}}>
                                <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Drivers of Detraction</div>
                                {sc.drivers.detraction.map((q,i)=>(<div key={i} style={{fontSize:12,color:C.text,fontStyle:"italic",borderLeft:`2px solid ${C.accent}`,paddingLeft:10,marginBottom:6}}>"{q}"</div>))}
                              </div>
                            )}
                          </div>
                        );
                      })() : null}

                      {/* Executive Action Summary */}
                      {content.actionSummary && (content.actionSummary.doNow?.length || content.actionSummary.doNext?.length || content.actionSummary.protect?.length) ? (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Executive Action Summary</h4>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:10}}>
                            {[{head:"Do Now",color:"#EF4444",items:content.actionSummary.doNow},{head:"Do Next",color:C.accent,items:content.actionSummary.doNext},{head:"Protect",color:"#22C55E",items:content.actionSummary.protect}].map(col=>(
                              <div key={col.head} style={{padding:"10px 12px",background:C.surface,borderRadius:8}}>
                                <div style={{fontSize:11,fontWeight:700,color:col.color,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{col.head}</div>
                                {(col.items||[]).map((it,j)=>(<div key={j} style={{fontSize:12,color:C.text,lineHeight:1.45,marginBottom:4}}>• {it}</div>))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Engagement metrics */}
                      {content.engagementMetrics && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Engagement Metrics</h4>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:10}}>
                            {[
                              {label:"Avg WPM", val:content.engagementMetrics.avgWpm},
                              {label:"Avg Sentiment", val:content.engagementMetrics.avgSentimentScore != null ? Number(content.engagementMetrics.avgSentimentScore).toFixed(2) : "—"},
                              {label:"Facial Engagement", val:content.engagementMetrics.avgFacialEngagement != null ? `${Math.round(content.engagementMetrics.avgFacialEngagement)}/100` : "n/a"},
                              {label:"Facial Positivity", val:content.engagementMetrics.avgFacialPositivity != null ? `${Math.round(content.engagementMetrics.avgFacialPositivity)}/100` : "n/a"},
                              {label:"Completion", val:content.engagementMetrics.completionRate || "—"},
                            ].map(m=>(
                              <div key={m.label} style={{padding:"10px 12px",background:C.surface,borderRadius:8,textAlign:"center"}}>
                                <div style={{fontSize:18,fontWeight:800,color:C.text}}>{m.val ?? "—"}</div>
                                <div style={{fontSize:10,color:C.muted,marginTop:2,textTransform:"uppercase",letterSpacing:0.5}}>{m.label}</div>
                              </div>
                            ))}
                          </div>
                          {content.engagementMetrics.narrative && (
                            <div style={{marginTop:10,fontSize:12,color:C.muted,lineHeight:1.5}}>{content.engagementMetrics.narrative}</div>
                          )}
                        </div>
                      )}

                      {/* Key Themes */}
                      {content.keyThemes?.length > 0 && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Key Themes</h4>
                          <div style={{display:"flex",flexDirection:"column",gap:10}}>
                            {content.keyThemes.map((t,i)=>(
                              <div key={i} style={{padding:"12px 14px",background:C.surface,borderRadius:8}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                                  <span style={{fontSize:13,fontWeight:600,color:C.text}}>{t.theme}</span>
                                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                                    {t.confidence && <span style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,padding:"2px 7px",borderRadius:20,border:`1px solid ${t.confidence==="high"?"#22C55E":t.confidence==="low"?"#EF4444":"#F59E0B"}`,color:t.confidence==="high"?"#22C55E":t.confidence==="low"?"#EF4444":"#F59E0B"}}>{t.confidence}</span>}
                                    <span style={{fontSize:11,color:t.sentiment==="positive"?"#22C55E":t.sentiment==="negative"?"#EF4444":C.muted}}>{t.frequency}</span>
                                  </div>
                                </div>
                                <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{t.description}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Drivers & Barriers */}
                      {content.driversAndBarriers && (content.driversAndBarriers.drivers?.length || content.driversAndBarriers.barriers?.length) ? (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Drivers &amp; Barriers</h4>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))",gap:10}}>
                            {[{head:"Drivers",color:"#22C55E",items:content.driversAndBarriers.drivers},{head:"Barriers",color:"#EF4444",items:content.driversAndBarriers.barriers}].map(col=>(
                              <div key={col.head} style={{padding:"10px 12px",background:C.surface,borderRadius:8}}>
                                <div style={{fontSize:11,fontWeight:700,color:col.color,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{col.head}</div>
                                {(col.items||[]).map((it,j)=>(<div key={j} style={{fontSize:12,color:C.text,lineHeight:1.45,marginBottom:4}}>• {it}</div>))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Question Analysis */}
                      {content.questionAnalysis?.length > 0 && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Question Analysis</h4>
                          <div style={{display:"flex",flexDirection:"column",gap:14}}>
                            {content.questionAnalysis.map((qa,i)=>(
                              <div key={i} style={{padding:"14px 16px",background:C.surface,borderRadius:8,border:`1px solid ${C.border}`}}>
                                <div style={{fontSize:13,fontWeight:700,color:C.info,marginBottom:6}}>Q{qa.questionIndex+1}: {qa.questionText}</div>
                                {qa.quantitativeStat && <div style={{fontSize:12,color:C.accent,fontWeight:600,marginBottom:6}}>{qa.quantitativeStat}</div>}
                                <div style={{fontSize:12,color:C.text,lineHeight:1.6,marginBottom:8}}>{qa.summary}</div>
                                {qa.sentimentBreakdown && (
                                  <div style={{display:"flex",gap:12,marginBottom:6}}>
                                    {Object.entries(qa.sentimentBreakdown).map(([k,v])=>(
                                      <span key={k} style={{fontSize:11,color:k==="positive"?"#22C55E":k==="negative"?"#EF4444":C.muted}}>
                                        {k}: <strong>{v}</strong>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {qa.notableQuotes?.length > 0 && (
                                  <div style={{marginTop:6}}>
                                    {qa.notableQuotes.map((q,j)=>(
                                      <div key={j} style={{padding:"8px 12px",borderLeft:`3px solid ${C.accent}`,background:C.card,borderRadius:"0 6px 6px 0",fontSize:12,color:C.text,fontStyle:"italic",lineHeight:1.5,marginBottom:4}}>
                                        "{q}"
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {qa.insights && <div style={{marginTop:6,fontSize:12,color:C.muted,lineHeight:1.5}}><strong>Insight:</strong> {qa.insights}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Verbatim Highlights */}
                      {content.verbatimHighlights?.length > 0 && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Verbatim Highlights</h4>
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            {content.verbatimHighlights.map((vh,i)=>(
                              <div key={i} style={{padding:"10px 14px",borderLeft:`3px solid ${vh.sentiment==="positive"?"#22C55E":vh.sentiment==="negative"?"#EF4444":C.accent}`,background:C.surface,borderRadius:"0 8px 8px 0"}}>
                                <div style={{fontSize:13,color:C.text,fontStyle:"italic",lineHeight:1.5,marginBottom:4}}>"{vh.quote}"</div>
                                <div style={{fontSize:11,color:C.muted}}>{vh.respondent} · {vh.question}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sentiment Analysis */}
                      {content.sentimentAnalysis && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Sentiment Analysis</h4>
                          <div style={{display:"flex",gap:16,marginBottom:10}}>
                            <div style={{fontSize:13,color:C.text}}>Overall: <strong style={{color:content.sentimentAnalysis.overall==="positive"?"#22C55E":content.sentimentAnalysis.overall==="negative"?"#EF4444":C.muted}}>{content.sentimentAnalysis.overall}</strong></div>
                            {content.sentimentAnalysis.overallScore != null && <div style={{fontSize:13,color:C.muted}}>Score: {Number(content.sentimentAnalysis.overallScore).toFixed(2)}</div>}
                          </div>
                          {content.sentimentAnalysis.narrative && <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{content.sentimentAnalysis.narrative}</div>}
                        </div>
                      )}

                      {/* Demographic Insights */}
                      {content.demographicInsights && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Demographic Insights</h4>
                          <div style={{fontSize:13,color:C.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{content.demographicInsights}</div>
                        </div>
                      )}

                      {/* Recommendations */}
                      {content.recommendations?.length > 0 && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:"#F59E0B",marginBottom:12,textTransform:"uppercase",letterSpacing:0.5}}>Recommendations</h4>
                          <div style={{display:"flex",flexDirection:"column",gap:10}}>
                            {content.recommendations.map((r,i)=>(
                              <div key={i} style={{padding:"12px 14px",background:C.surface,borderRadius:8,borderLeft:`3px solid ${r.priority==="high"?"#EF4444":r.priority==="medium"?"#F59E0B":"#22C55E"}`}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                                  <span style={{fontSize:13,fontWeight:700,color:C.text}}>{r.title}</span>
                                  <div style={{display:"flex",gap:6}}>
                                    <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,
                                      background:r.priority==="high"?"#EF444422":r.priority==="medium"?"#F59E0B22":"#22C55E22",
                                      color:r.priority==="high"?"#EF4444":r.priority==="medium"?"#F59E0B":"#22C55E",
                                      textTransform:"uppercase"}}>{r.priority}</span>
                                    {r.timeframe && <span style={{fontSize:10,color:C.muted,fontWeight:600}}>{r.timeframe.replace(/_/g," ")}</span>}
                                  </div>
                                </div>
                                <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{r.description}</div>
                                <div style={{display:"flex",gap:14,marginTop:6,flexWrap:"wrap"}}>
                                  {r.impact && <span style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>Impact: {r.impact}</span>}
                                  {r.effort && <span style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>Effort: {r.effort}</span>}
                                  {r.owner && <span style={{fontSize:10,color:C.info,fontWeight:600,textTransform:"uppercase",letterSpacing:0.4}}>Owner: {r.owner}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Conclusion */}
                      {content.conclusion && (
                        <div style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>Conclusion</h4>
                          <div style={{fontSize:13,color:C.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{content.conclusion}</div>
                        </div>
                      )}

                      {/* Methodology */}
                      {content.methodologyNote && (
                        <div style={{padding:"14px 18px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
                          <h4 style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Methodology</h4>
                          <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>{content.methodologyNote}</div>
                        </div>
                      )}

                      {/* ── Generic fallback: any keys Claude returned that aren't standard ── */}
                      {(()=>{
                        const KNOWN = new Set(["executiveSummary","actionSummary","scorecard","respondentProfile","engagementMetrics","keyThemes","driversAndBarriers","questionAnalysis","verbatimHighlights","sentimentAnalysis","demographicInsights","recommendations","conclusion","methodologyNote","_stats","surveyTitle","completedAt","totalParticipants","overallSentiment","questionInsights","strategicRecommendations"]);
                        const camelToTitle = k => k.replace(/([A-Z])/g," $1").replace(/^./,c=>c.toUpperCase()).trim();
                        const renderGenericValue = (val) => {
                          if (val === null || val === undefined) return null;
                          if (typeof val === "string" || typeof val === "number") {
                            return <div style={{fontSize:13,color:C.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{String(val)}</div>;
                          }
                          if (Array.isArray(val)) {
                            if (val.length === 0) return null;
                            if (typeof val[0] === "string") {
                              return <ul style={{margin:0,padding:"0 0 0 16px"}}>{val.map((s,i)=><li key={i} style={{fontSize:13,color:C.text,lineHeight:1.7,marginBottom:4}}>{s}</li>)}</ul>;
                            }
                            return val.map((item,i)=>(
                              <div key={i} style={{padding:"10px 12px",background:C.surface,borderRadius:6,marginBottom:8,fontSize:12,color:C.text}}>
                                {Object.entries(item).map(([k,v])=>(
                                  <div key={k} style={{marginBottom:4}}><span style={{color:C.muted,marginRight:6}}>{camelToTitle(k)}:</span>{typeof v==="object"?JSON.stringify(v):String(v??"")} </div>
                                ))}
                              </div>
                            ));
                          }
                          if (typeof val === "object") {
                            return Object.entries(val).map(([k,v])=>(
                              <div key={k} style={{marginBottom:6}}><span style={{fontSize:11,color:C.muted,display:"block",marginBottom:2}}>{camelToTitle(k)}</span><span style={{fontSize:13,color:C.text}}>{typeof v==="object"?JSON.stringify(v):String(v??"")}</span></div>
                            ));
                          }
                          return <div style={{fontSize:13,color:C.text}}>{String(val)}</div>;
                        };
                        // Skip stray keys with no real content (e.g. an LLM-emitted
                        // empty "executiveSummary2") so they don't render a blank section.
                        const isEmptyVal = (v) => {
                          if (v === null || v === undefined) return true;
                          if (typeof v === "string") return v.trim() === "";
                          if (Array.isArray(v)) return v.length === 0;
                          if (typeof v === "object") return Object.keys(v).length === 0;
                          return false;
                        };
                        const customEntries = Object.entries(content).filter(([k,v])=>!KNOWN.has(k) && !isEmptyVal(v));
                        if (customEntries.length === 0) return null;
                        return customEntries.map(([key,val])=>(
                          <div key={key} style={{padding:"18px 20px",borderRadius:10,background:C.card,border:`1px solid ${C.border}`}}>
                            <h4 style={{fontSize:14,fontWeight:700,color:C.accent,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>{camelToTitle(key)}</h4>
                            {renderGenericValue(val)}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── PROGRESS TAB ────────────────────────────────────────────── */}
          {detailTab === "progress" && (() => {
            const target = survey.targetSampleSize || 0;
            const qualifying = progress?.qualifyingCount ?? 0;
            const pct = target > 0 ? Math.min(100, Math.round((qualifying / target) * 100)) : 0;
            // Live server-configured mode (authoritative). Fall back to the mode
            // recorded on an existing session row if the server didn't report it.
            const qcMode = progress?.qualityCheckMode || progress?.sessions?.[0]?.sessionQuality?.qualityMode;
            const qcActive = qcMode && qcMode !== "disabled" && qcMode !== "silent";
            const contentJudge = progress?.contentJudge || "disabled";
            const contentJudgeOn = contentJudge === "enabled";
            const flagColor = { pass:"#22C55E", review:"#F59E0B", fail:"#EF4444" };

            // ── Group sessions by participant ──────────────────────────────
            // One headline row per participant; multiple attempts collapse and
            // expand on click. Headline status priority surfaces the most
            // meaningful disposition: a completion always wins, else the
            // furthest terminal state. Tie-break by most recent attempt.
            //
            // Sessions sourced from a supplier panel (source: "external") have
            // no `participant` relation — they're identified by
            // externalParticipantId instead. Without this fallback the group
            // key (and the displayed label) collapsed to the raw session UUID,
            // showing meaningless strings like "d0e03864" instead of the
            // supplier's participant ID.
            const STATUS_RANK = { completed:0, screened_out:1, in_progress:2, abandoned:3 };
            const rankOf = st => (STATUS_RANK[st] ?? 99);
            const participantKey = s => s.participant?.id || s.externalParticipantId || s.id;
            const participantLabel = s => s.participant?.name || s.participant?.email || s.externalParticipantId || s.id.slice(0,8);
            const groups = (() => {
              if (!progress) return [];
              const m = new Map(); // participant key → session[]  (insertion order = server createdAt desc)
              for (const s of progress.sessions) {
                const key = participantKey(s);
                if (!m.has(key)) m.set(key, []);
                m.get(key).push(s);
              }
              return Array.from(m.entries()).map(([key, list]) => {
                const sorted = [...list].sort((a,b) => {
                  const r = rankOf(a.status) - rankOf(b.status);
                  if (r !== 0) return r;
                  return new Date(b.createdAt||0) - new Date(a.createdAt||0);
                });
                return { key, headline: sorted[0], attempts: sorted };
              });
            })();

            // Summary tiles reflect one participant, one vote — tallied off each
            // group's headline attempt (not every raw session) so a participant
            // who abandoned twice before completing isn't counted as 2 abandons
            // and a completion, and someone who abandoned twice with no
            // completion is counted once, not twice.
            const sc = {};
            const qc = {};
            groups.forEach(({ headline }) => {
              sc[headline.status] = (sc[headline.status] || 0) + 1;
              const flag = headline.sessionQuality?.sessionQualityFlag;
              if (flag) qc[flag] = (qc[flag] || 0) + 1;
            });

            const statusBadge = (st) => (
              <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:99,
                background:st==="completed"?"#22C55E22":st==="abandoned"?"#EF444422":st==="screened_out"?"#64748B22":"#3B82F622",
                color:st==="completed"?"#22C55E":st==="abandoned"?"#EF4444":st==="screened_out"?"#94A3B8":"#3B82F6"}}>
                {st}
              </span>
            );

            // Renders the 6 cells after the Participant cell — shared by headline
            // and expanded attempt rows so a flagged attempt stays actionable.
            const sessionCells = (s) => {
              const q = s.sessionQuality;
              const flag = q?.sessionQualityFlag;
              const rev  = q?.adminReviewStatus;
              const decided = rev === "approved" || rev === "rejected";
              const isReviewing = reviewingId === s.id;
              const fColor = flagColor[flag] || C.muted;
              return (
                <>
                  <td style={{padding:"10px 12px"}}>{statusBadge(s.status)}</td>
                  <td style={{padding:"10px 12px",color:C.muted,whiteSpace:"nowrap"}}>
                    {s.durationSecs != null ? `${Math.floor(s.durationSecs/60)}m ${s.durationSecs%60}s` : "—"}
                  </td>
                  <td style={{padding:"10px 12px",color:C.text,whiteSpace:"nowrap"}}>
                    {q ? `${Math.round(q.sessionQualityScore ?? 0)}` : <span style={{color:C.muted}}>—</span>}
                  </td>
                  <td style={{padding:"10px 12px"}}>
                    {flag ? (
                      <span title={(q.reasonCodes ? JSON.parse(q.reasonCodes||"[]") : []).join(", ")}
                        style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:99,background:fColor+"22",color:fColor,cursor:"help"}}>
                        {flag}
                      </span>
                    ) : <span style={{color:C.muted}}>—</span>}
                  </td>
                  <td style={{padding:"10px 12px"}}>
                    {rev && (flag === "review" || flag === "fail") ? (
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:99,
                        background:rev==="approved"?"#22C55E22":rev==="rejected"?"#EF444422":"#64748B22",
                        color:rev==="approved"?"#22C55E":rev==="rejected"?"#EF4444":"#64748B"}}>
                        {rev}
                      </span>
                    ) : <span style={{color:C.muted}}>—</span>}
                  </td>
                  <td style={{padding:"10px 12px",whiteSpace:"nowrap"}}>
                    {q && (flag === "review" || flag === "fail") ? (
                      <div style={{display:"flex",gap:5}}>
                        <button onClick={(e)=>{e.stopPropagation(); handleReview(s.id,"approved");}} disabled={isReviewing||decided}
                          style={{padding:"3px 10px",borderRadius:5,fontSize:11,fontWeight:600,cursor:decided?"default":"pointer",fontFamily:"inherit",border:"1px solid #22C55E",background:"#22C55E22",color:"#22C55E",opacity:decided&&rev!=="approved"?0.35:1}}>
                          {isReviewing?"…":"Approve"}
                        </button>
                        <button onClick={(e)=>{e.stopPropagation(); handleReview(s.id,"rejected");}} disabled={isReviewing||decided}
                          style={{padding:"3px 10px",borderRadius:5,fontSize:11,fontWeight:600,cursor:decided?"default":"pointer",fontFamily:"inherit",border:"1px solid #EF4444",background:"#EF444422",color:"#EF4444",opacity:decided&&rev!=="rejected"?0.35:1}}>
                          {isReviewing?"…":"Reject"}
                        </button>
                      </div>
                    ) : <span style={{color:C.muted,fontSize:11}}>—</span>}
                  </td>
                </>
              );
            };

            return (
              <div>
                {/* QC mode notice */}
                {(!progress || !qcActive) && (
                  <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,background:"#F59E0B12",border:"1px solid #F59E0B33",fontSize:12,color:"#F59E0B",lineHeight:1.5}}>
                    ⚠ Quality checks are in <strong>{qcMode || "disabled"}</strong> mode — session flags are not being recorded.
                    Set <code style={{background:"#F59E0B22",padding:"1px 5px",borderRadius:3}}>QUALITY_CHECK_MODE=shadow</code> or <code style={{background:"#F59E0B22",padding:"1px 5px",borderRadius:3}}>enabled</code> in <code>.env</code> to activate.
                  </div>
                )}

                {/* Quality-check config status */}
                {progress && (
                  <div style={{marginBottom:16,display:"flex",gap:18,flexWrap:"wrap",fontSize:12,color:C.muted}}>
                    <span>Quality check: <strong style={{color:qcActive?C.teal:C.muted,textTransform:"capitalize"}}>{qcMode || "disabled"}</strong></span>
                    <span title={contentJudgeOn
                        ? "Open-ended answers are scored by Claude Haiku (effort/relevance/completeness) → response_qc table. Advisory only — does not gate payouts."
                        : "AI content scoring of open-ended answers is off. Set QUALITY_CONTENT_JUDGE=enabled to activate."}>
                      AI content judge: <strong style={{color:contentJudgeOn?C.teal:C.muted,textTransform:"capitalize"}}>{contentJudge}</strong>
                    </span>
                  </div>
                )}

                {/* Completion bar */}
                <div style={{marginBottom:20,padding:"16px 18px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10}}>
                    <span style={{fontSize:14,fontWeight:700,color:C.text}}>Survey Progress</span>
                    <span style={{fontSize:13,color:pct>=100?C.success:C.accent,fontWeight:700}}>{pct}%{target>0?` · ${qualifying} / ${target}`:" (no target set)"}</span>
                  </div>
                  <div style={{height:10,borderRadius:5,background:C.border,overflow:"hidden",marginBottom:10}}>
                    <div style={{height:"100%",width:`${pct}%`,borderRadius:5,background:pct>=100?C.success:C.accent,transition:"width 0.4s"}}/>
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    {[
                      {label:"Completed",     val: sc.completed||0,         color:C.success},
                      {label:"Abandoned",     val: sc.abandoned||0,         color:C.muted},
                      {label:"Screened Out",  val: sc.screened_out||0,      color:"#94A3B8"},
                      {label:"In Progress",   val: sc.in_progress||0,       color:C.info},
                      {label:"QC Pass",       val: qc.pass||0,              color:"#22C55E"},
                      {label:"QC Review",     val: qc.review||0,            color:"#F59E0B"},
                      {label:"QC Fail",       val: qc.fail||0,              color:"#EF4444"},
                    ].map(s=>(
                      <div key={s.label} style={{textAlign:"center",minWidth:72}}>
                        <div style={{fontSize:18,fontWeight:700,color:s.color}}>{s.val}</div>
                        <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Manual close + reload */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["all","pass","review","fail"].map(f=>(
                      <button key={f} onClick={()=>handleFilterChange(f)}
                        style={{padding:"5px 12px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
                          border:`1px solid ${progressFilter===f?flagColor[f]||C.accent:C.border}`,
                          background:progressFilter===f?(flagColor[f]||C.accent)+"22":"transparent",
                          color:progressFilter===f?(flagColor[f]||C.accent):C.muted}}>
                        {f==="all"?"All":f.charAt(0).toUpperCase()+f.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <button onClick={()=>loadProgress(progressFilter)} disabled={progressLoading}
                      style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",border:`1px solid ${C.border}`,background:"transparent",color:C.muted}}>
                      {progressLoading?"Loading…":"↻ Refresh"}
                    </button>
                    {survey.status === "active" && (
                      <button onClick={handleManualClose} disabled={closingManual}
                        style={{padding:"5px 14px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",border:`1px solid ${C.danger}`,background:"transparent",color:C.danger}}>
                        {closingManual?"Closing…":"Close Survey Now"}
                      </button>
                    )}
                    {survey.status === "closed" && (
                      <span style={{fontSize:11,color:C.muted}}>Closed {fmt(survey.closedAt)}</span>
                    )}
                  </div>
                </div>

                {/* Sessions table */}
                {progressLoading ? <Spinner/> : !progress ? (
                  <EmptyState message="Click Refresh to load sessions"/>
                ) : progress.sessions.length === 0 ? (
                  <EmptyState message={progressFilter==="all"?"No sessions yet for this survey":`No ${progressFilter} sessions`}/>
                ) : (
                  <div style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}>
                    <table style={{width:"100%",borderCollapse:"collapse",background:C.card,fontSize:12}}>
                      <thead>
                        <tr style={{borderBottom:`1px solid ${C.border}`}}>
                          {["Participant","Status","Duration","QC Score","Flag","Review Status","Actions"].map(h=>(
                            <th key={h} style={{padding:"10px 12px",textAlign:"left",color:C.muted,fontWeight:600,fontSize:11,textTransform:"uppercase",letterSpacing:0.5,whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map(({ key, headline, attempts }) => {
                          const multi = attempts.length > 1;
                          const expanded = !!expandedGroups[key];
                          const toggle = () => multi && setExpandedGroups(g => ({ ...g, [key]: !g[key] }));
                          const isExternal = !headline.participant && !!headline.externalParticipantId;
                          return (
                            <React.Fragment key={key}>
                              <tr onClick={toggle}
                                style={{borderBottom:`1px solid ${C.border}33`,cursor:multi?"pointer":"default",background:multi&&expanded?C.surface:"transparent"}}>
                                <td style={{padding:"10px 12px",color:C.text,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {multi && (
                                    <span style={{display:"inline-block",width:12,color:C.muted,transition:"transform 0.15s",transform:expanded?"rotate(90deg)":"none"}}>▸</span>
                                  )}
                                  <span style={{marginLeft:multi?4:16, fontFamily: isExternal ? "monospace" : "inherit"}}>{participantLabel(headline)}</span>
                                  {isExternal && (
                                    <span title={headline.supplier?.name ? `Supplied by ${headline.supplier.name}` : "External panel participant"}
                                      style={{marginLeft:8,fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:99,background:"#3B82F622",color:"#3B82F6",cursor:"help"}}>
                                      {headline.supplier?.name || "External"}
                                    </span>
                                  )}
                                  {multi && (
                                    <span style={{marginLeft:8,fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:99,background:C.border,color:C.muted}}>
                                      ·{attempts.length} attempts
                                    </span>
                                  )}
                                </td>
                                {sessionCells(headline)}
                              </tr>
                              {multi && expanded && attempts.map(s => (
                                <tr key={s.id} style={{borderBottom:`1px solid ${C.border}22`,background:C.surface+"80"}}>
                                  <td style={{padding:"8px 12px 8px 28px",color:C.muted,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:11}}>
                                    <span style={{color:C.border,marginRight:6}}>└</span>
                                    {fmt(s.createdAt)} · {s.id.slice(0,8)}
                                  </td>
                                  {sessionCells(s)}
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{padding:"10px 14px",fontSize:11,color:C.muted,borderTop:`1px solid ${C.border}33`}}>
                      Showing {groups.length} participant{groups.length===1?"":"s"} · {progress.sessions.length} of {progress.total} sessions
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── AGGREGATE TAB ──────────────────────────────────────────── */}
          {detailTab === "aggregate" && (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:C.text}}>Aggregate Pipeline Status</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>Sessions contributing to the report aggregate</div>
                </div>
                <button onClick={loadAggStatus} disabled={aggStatusLoading}
                  style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,cursor:"pointer"}}>
                  {aggStatusLoading ? "Refreshing…" : "Refresh"}
                </button>
              </div>

              {aggStatusLoading && !aggStatus && <div style={{color:C.muted,fontSize:13}}>Loading…</div>}

              {aggStatus && (() => {
                const { included, queued, pendingReview, excluded, staleQueued } = aggStatus;
                const stats = [
                  { label:"Included in aggregate", val: included,      color: C.teal   },
                  { label:"Queued (processing)",   val: queued,        color: queued > 0 ? "#F59E0B" : C.muted },
                  { label:"Pending review",        val: pendingReview, color: pendingReview > 0 ? "#F59E0B" : C.muted },
                  { label:"Excluded",              val: excluded,      color: C.muted  },
                ];
                return (<>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
                    {stats.map(s => (
                      <div key={s.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 18px"}}>
                        <div style={{fontSize:28,fontWeight:700,color:s.color}}>{s.val}</div>
                        <div style={{fontSize:11,color:C.muted,marginTop:4}}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {staleQueued?.length > 0 && (
                    <div style={{background:"#F59E0B18",border:"1px solid #F59E0B55",borderRadius:10,padding:"16px 20px",marginBottom:20}}>
                      <div style={{fontSize:13,fontWeight:600,color:"#F59E0B",marginBottom:6}}>
                        {staleQueued.length} session{staleQueued.length > 1 ? "s" : ""} stuck in queue
                      </div>
                      <div style={{fontSize:12,color:C.muted,marginBottom:12}}>
                        These sessions completed but their aggregate update did not finish (likely a server restart mid-processing).
                        Use "Retry Now" to reprocess them — the operation is safe to run multiple times.
                      </div>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <button onClick={handleRecoverAggregations} disabled={recovering}
                          style={{fontSize:13,fontWeight:600,padding:"8px 18px",borderRadius:7,border:"none",
                            background:"#F59E0B",color:"#000",cursor:recovering?"not-allowed":"pointer",opacity:recovering?0.6:1}}>
                          {recovering ? "Retrying…" : "Retry Now"}
                        </button>
                        <span style={{fontSize:11,color:C.muted}}>Session IDs: {staleQueued.map(s=>s.sessionId.slice(0,8)+"…").join(", ")}</span>
                      </div>
                      {recoveryResult && (
                        <div style={{marginTop:10,fontSize:12,color: recoveryResult.failed === 0 ? C.teal : C.danger}}>
                          Recovery complete: {recoveryResult.succeeded}/{recoveryResult.attempted} succeeded
                          {recoveryResult.failed > 0 ? ` · ${recoveryResult.failed} failed (check server logs)` : ""}
                        </div>
                      )}
                    </div>
                  )}

                  {staleQueued?.length === 0 && queued === 0 && (
                    <div style={{fontSize:13,color:C.teal,padding:"12px 16px",background:"#22D3EE11",borderRadius:8,border:"1px solid #22D3EE33"}}>
                      Pipeline healthy — no stuck sessions.
                    </div>
                  )}
                </>);
              })()}
            </div>
          )}

          {/* ── APPROVAL TRAIL TAB ──────────────────────────────────────────── */}
          {detailTab === "trail" && (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:C.text}}>Approval Trail</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>Full history of review, edit, and approval events</div>
                </div>
                {/* Send for Review panel — draft surveys only */}
                {survey && survey.status === "draft" && (
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                    <div style={{display:"flex",gap:8}}>
                      <input value={reviewNote} onChange={e=>setReviewNote(e.target.value)}
                        placeholder="Optional note to customer…"
                        style={{padding:"7px 12px",borderRadius:7,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:12,fontFamily:"inherit",outline:"none",width:240}}/>
                      <button onClick={handleSendForReview} disabled={sendingForReview}
                        style={{padding:"7px 14px",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid #8B5CF6`,background:"#8B5CF618",color:"#8B5CF6",fontFamily:"inherit",opacity:sendingForReview?0.5:1,whiteSpace:"nowrap"}}>
                        {sendingForReview ? "Sending…" : "→ Send for Review"}
                      </button>
                    </div>
                    {reviewErr && <div style={{fontSize:11,color:C.danger}}>{reviewErr}</div>}
                  </div>
                )}
              </div>

              {approvalLoading && <div style={{color:C.muted,fontSize:13}}>Loading…</div>}
              {!approvalLoading && approvalEvents.length === 0 && (
                <div style={{color:C.muted,fontSize:13,padding:"24px 0",textAlign:"center"}}>No approval events yet. Send the survey for review to start the trail.</div>
              )}
              {approvalEvents.map((ev, i) => {
                const evColor = {sent_for_review:"#8B5CF6",approved:"#22C55E",rejected:"#EF4444",edit:"#F59E0B",published:"#3B82F6"}[ev.event] || C.muted;
                const evLabel = {sent_for_review:"Sent for Review",approved:"Approved",rejected:"Sent Back",edit:"Edited",published:"Published"}[ev.event] || ev.event;
                let diffItems = [];
                if (ev.snapshot) { try { const d = JSON.parse(ev.snapshot); diffItems = Object.entries(d); } catch {} }
                return (
                  <div key={ev.id} style={{display:"flex",gap:14,marginBottom:16}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:evColor,marginTop:4}}/>
                      {i < approvalEvents.length-1 && <div style={{width:2,flex:1,background:C.border,marginTop:4}}/>}
                    </div>
                    <div style={{flex:1,paddingBottom:16}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                        <span style={{fontSize:13,fontWeight:700,color:evColor}}>{evLabel}</span>
                        <span style={{fontSize:11,color:C.muted}}>by {ev.actorName} ({ev.actorType})</span>
                        <span style={{fontSize:11,color:C.muted,marginLeft:"auto"}}>{new Date(ev.createdAt).toLocaleString()}</span>
                      </div>
                      {ev.note && <div style={{fontSize:12,color:C.muted,fontStyle:"italic",marginBottom:4}}>"{ev.note}"</div>}
                      {diffItems.length > 0 && (
                        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 12px"}}>
                          {diffItems.map(([field, change]) => (
                            <div key={field} style={{fontSize:11,color:C.muted,padding:"3px 0",borderBottom:`1px solid ${C.border}`}}>
                              <span style={{fontWeight:600,color:C.text}}>{field}:</span>{" "}
                              {change === "updated" ? <span style={{color:"#F59E0B"}}>questions updated</span> : (
                                <><span style={{textDecoration:"line-through",opacity:0.6}}>{String(change.from)}</span> → <span style={{color:C.text}}>{String(change.to)}</span></>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{marginTop:8}}>
                <button onClick={loadApprovalTrail} disabled={approvalLoading}
                  style={{fontSize:12,padding:"6px 14px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,cursor:"pointer"}}>
                  {approvalLoading ? "Refreshing…" : "↻ Refresh"}
                </button>
              </div>
            </div>
          )}

        </>)}
      </div>
    </div>
  );
}

// ── CustomerCenter main component ─────────────────────────────────────────────
const TABS = [
  { key:"overview",   label:"Overview"      },
  { key:"projects",   label:"Projects"      },
  { key:"agreements", label:"Agreements"    },
  { key:"contacts",   label:"Contacts"      },
  { key:"invoices",   label:"Invoices"      },
  { key:"portal",     label:"Portal Access" },
];

export default function CustomerCenter({ customerId, onClose, openSurveyId, openSurveyStatus }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState("overview");
  const [err, setErr]           = useState("");
  // Survey wizard state — null = closed, { projectId, prefill } = open
  const [surveyWizard,  setSurveyWizard]  = useState(null);
  // Survey detail panel — null = closed, surveyId string = open (read-only, non-draft)
  const [surveyDetail,  setSurveyDetail]  = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const autoOpenDone = React.useRef(false);

  // Always fetch the full survey (questions + all targeting) before opening the wizard
  const openSurveyForEdit = async (projectId, surveyId) => {
    setWizardLoading(true);
    try {
      const { survey } = await api("GET", `/surveys/${surveyId}`);
      setSurveyWizard({ projectId, prefill: { editId: survey.id, ...survey } });
    } catch(e) { alert(`Failed to load survey: ${e.message}`); }
    finally { setWizardLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const data = await api("GET", `/customers/${customerId}/full`);
      setCustomer(data.customer);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // Auto-open survey when navigated from the global Surveys tab
  useEffect(() => {
    if (!openSurveyId || autoOpenDone.current) return;
    autoOpenDone.current = true;
    if (openSurveyStatus === "draft") openSurveyForEdit(null, openSurveyId);
    else setSurveyDetail(openSurveyId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSurveyId, openSurveyStatus]);

  if (err) return (
    <div style={{padding:32,textAlign:"center",color:C.danger}}>
      {err} <Btn variant="ghost" style={{marginLeft:12}} onClick={load}>Retry</Btn>
    </div>
  );

  // Loading overlay while fetching full survey before opening wizard
  if (wizardLoading) return (
    <div style={{background:C.bg,minHeight:"100dvh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Poppins',sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <Spinner/>
        <div style={{color:C.muted,fontSize:13,marginTop:12}}>Loading survey…</div>
      </div>
    </div>
  );

  // Survey detail panel (read-only, non-draft)
  if (surveyDetail) return (
    <SurveyDetailPanel
      surveyId={surveyDetail}
      onClose={()=>setSurveyDetail(null)}
      onRefresh={()=>{ setSurveyDetail(null); load(); }}
    />
  );

  // Survey wizard fullscreen overlay (draft only)
  if (surveyWizard) return (
    <SurveyWizard
      customerId={customerId}
      projectId={surveyWizard.projectId}
      projects={customer?.projects||[]}
      prefill={surveyWizard.prefill}
      onDone={()=>{ setSurveyWizard(null); load(); }}
      onCancel={()=>setSurveyWizard(null)}
    />
  );

  return (
    <div style={{background:C.bg,minHeight:"100dvh",fontFamily:"'Poppins',sans-serif"}}>
      <style>{CC_STYLES}</style>

      {/* Header */}
      <div className="cc-header">
        <button onClick={onClose} style={{background:"transparent",border:"none",color:C.muted,cursor:"pointer",fontSize:22,lineHeight:1,display:"flex",alignItems:"center",padding:4,minWidth:44,minHeight:44,flexShrink:0}}>←</button>
        {customer ? (
          <div style={{flex:1,minWidth:0}}>
            <div className="cc-header-meta">
              <span style={{fontWeight:700,fontSize:16,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{customer.companyName}</span>
              <span style={{fontSize:11,color:C.muted,background:C.card,padding:"2px 8px",borderRadius:99,border:`1px solid ${C.border}`,flexShrink:0}}>{customer.plan||"standard"}</span>
              {customer.industry&&<span style={{fontSize:11,color:C.muted,flexShrink:0}}>{customer.industry}</span>}
            </div>
            {(customer.billingCity||customer.billingState||customer.industry)&&<div style={{fontSize:12,color:C.muted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{[customer.industry,customer.billingCity,customer.billingState].filter(Boolean).join(" · ")}</div>}
          </div>
        ) : <div style={{flex:1,color:C.muted,fontSize:14}}>Loading…</div>}
      </div>

      {/* Tabs */}
      <div className="cc-tab-bar">
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)} style={{
            background:"transparent",border:"none",cursor:"pointer",padding:"11px 14px",
            fontSize:13,fontWeight:600,fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0,
            color:tab===t.key?C.accent:C.muted,
            borderBottom:tab===t.key?`2px solid ${C.accent}`:"2px solid transparent",
            minHeight:44,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      <div className="cc-body">
        {loading ? <Spinner/> : customer ? (
          <>
            {tab==="overview"   && <OverviewTab   customer={customer} onRefresh={load}/>}
            {tab==="contacts"   && <ContactsTab   customer={customer} onRefresh={load}/>}
            {tab==="agreements" && <AgreementsTab customer={customer} onRefresh={load}/>}
            {tab==="projects"   && <ProjectsTab   customer={customer} onRefresh={load}
                onNewSurvey={(pid,s)=>{
                  if (!s) { setSurveyWizard({projectId:pid,prefill:null}); return; }
                  if (s.status==="draft") openSurveyForEdit(pid, s.id);
                  else setSurveyDetail(s.id);
                }}/>}

            {tab==="invoices"   && <InvoicesTab   customer={customer} onRefresh={load}/>}
            {tab==="portal"     && <PortalAccessTab customer={customer} onRefresh={load}/>}
          </>
        ) : null}
      </div>
    </div>
  );
}
